import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight, Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Button, IconButton, CircularProgress, Card, Box, Chip, Grid, Stack, CardContent, CardActions, Radio, RadioGroup, FormControlLabel, FormGroup, Checkbox } from "@mui/material";
import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowUpward, Edit } from "@mui/icons-material";
import { EditIcon } from "lucide-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { kioskModel, profileModel } from "../Axios/AxiosConfig";
import ReportStore from "../DataRepository/Reports";
import CollapsibleSection from "../Prescription/CollapsibleSection";
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import {
  Table,
  TableBody,
  Autocomplete,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import usePrescriptionStore from "../DataRepository/Prescription";
import LoadingComponent from "../loading"
import DoshaBarChart from "./VikritiChart";

import { CircularProgressbar, buildStyles } from "react-circular-progressbar";

import axios from "axios";
import { teleConsultationModel, shopModel } from "../Axios/AxiosConfig";
import VikritiStore from "../DataRepository/VikiritiStore";
import {
  Circle, AlertTriangle, Scale,
  Weight,
  Gauge,
  Droplets,
  Activity,
  Bone,
  Flame,
  User,
  Star,
  Zap,
  BarChart2,
  ClipboardList,
  FileText,
  FileBarChart2,
  HeartPulse,
  ScanFace
} from 'lucide-react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Helper function to format the body composition value with unit
const formatBodyCompValue = (value, unit) => {
  if (value === undefined || value === null || value === "") return '---';

  let formattedValue = value;

  // If value is a number, fix to 2 decimals
  if (!isNaN(parseFloat(value)) && isFinite(value)) {
    // If it's a string like "120/80", this will fail, which is correct
    formattedValue = parseFloat(value).toFixed(2);
  } else if (typeof value === 'string' && value.includes('/')) {
    // Handle Blood Pressure "systolic/diastolic"
    const parts = value.split('/');
    if (parts.length === 2) {
      const v1 = parseFloat(parts[0]);
      const v2 = parseFloat(parts[1]);
      if (!isNaN(v1) && !isNaN(v2)) {
        formattedValue = `${v1.toFixed(0)}/${v2.toFixed(0)}`; // BP usually doesn't need decimals, but let's keep it clean
      }
    }
  }

  return unit ? `${formattedValue} ${unit}` : formattedValue;
};



const headerCellStyle = {
  backgroundColor: '#fff8f0',
  fontWeight: 600,
  fontSize: 14,
  borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)',
  fontFamily: "'Poppins', sans-serif",
  color: 'var(--themePrimaryColor, #DA6E0B)',
  py: 2,
  textTransform: 'none',
  letterSpacing: '0.02em'
};

const getTrend = (before, after, threshold = 0.5) => {
  if (before == null || after == null) return '→';
  const b = parseFloat(before);
  const a = parseFloat(after);
  if (isNaN(b) || isNaN(a)) return '→';
  if (a - b > threshold) return '↑';
  if (b - a > threshold) return '↓';
  return '→';
};

const cleanJson = (str) => {
  try {
    if (!str || str.includes('AI analysis failed')) return null;
    return JSON.parse(str);
  } catch {
    return null;
  }
};
const PARAM_SYNONYMS = {
  haemoglobin: 'hemoglobin',
  hgb: 'hemoglobin',
  pcv: 'pcv',
  hct: 'pcv',
};

const normalizeName = (name = '') => {
  const key = name.trim().toLowerCase();
  return PARAM_SYNONYMS[key] || key;
};

// Component for body composition metric card
const BodyMetricCard = ({ icon, title, value, unit, status, range }) => {
  const [showRange, setShowRange] = useState(false);

  return (
    <div
      className="bg-[#f5f5f4] rounded-lg p-4 shadow-sm border border-gray-200 flex flex-col items-center text-center hover:bg-[#ececeb] transition-all cursor-pointer h-full justify-between"
      style={{ fontFamily: "'Poppins', sans-serif" }}
      onClick={() => setShowRange(!showRange)}
    >
      <div className="flex flex-col items-center w-full">
        <div className="p-2 bg-[#f5f5f4] rounded-full mb-2">
          {icon}
        </div>
        <p className="text-sm text-[color:var(--themePrimaryColor,#374151)] font-medium">{title}</p>

        <div className="mt-1 h-8 flex items-center justify-center">
          {showRange && range ? (
            <p className="text-xs font-medium text-[color:var(--themePrimaryColor,#DA6E0B)] animate-pulse">
              Range: {range}
            </p>
          ) : (
            <p className="text-md font-semibold text-gray-900">
              {formatBodyCompValue(value, unit)}
            </p>
          )}
        </div>
      </div>



      {status && (
        <span className={`mt-2 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${status === 'high' ? 'bg-red-100 text-red-800' :
          status === 'low' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
          {status}
        </span>
      )}
    </div>
  );
};

const getStatusTag = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'low':
      return <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 border border-yellow-300">Low</span>;
    case 'high':
      return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800 border border-red-300">High</span>;
    case 'normal':
      return <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800 border border-green-300">Normal</span>;
    case 'borderline':
      return <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800 border border-orange-300">Borderline</span>;
    case 'borderline_low':
      return <span className="px-2 py-0.5 text-xs rounded bg-yellow-200 text-yellow-900 border border-yellow-400">Borderline Low</span>;
    case 'borderline_high':
      return <span className="px-2 py-0.5 text-xs rounded bg-orange-200 text-orange-900 border border-orange-400">Borderline High</span>;
    case 'critical':
      return <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 border border-purple-300">Critical</span>;
    case 'missed':
      return <span className="px-2 py-0.5 text-xs rounded bg-[color:var(--themeSecondaryColor,#e5e7eb)] text-gray-600 border border-[color:var(--themePrimaryColor,#d1d5db)]">Missed</span>;
    default:
      return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-[color:var(--themePrimaryColor,#374151)] border border-gray-200">Unknown</span>;
  }
};


const getIcon = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'low':
    case 'high':
      return <AlertTriangle className="text-red-500 w-5 h-5 mr-2" />;
    case 'normal':
      return <CheckCircle className="text-green-500 w-5 h-5 mr-2" />;
  }
};

function Patient_Profile() {

  let { id } = useParams();

  let { selectedPatientdetails } = usePrescriptionStore();

  const [inputValue, setInputValue] = useState("");

  const [loading, setLoading] = useState(false);

  const { getAIReports, AIReports, isAIReportsLoading, getReports, Reports } = ReportStore();
  const [selectedBeforeReports, setSelectedBeforeReports] = useState([]);
  const [selectedAfterReports, setSelectedAfterReports] = useState([]);


  // Helper to check if a report is in either list
  const getReportSelection = (reportId) => {
    if (selectedBeforeReports.includes(reportId)) return 'before';
    if (selectedAfterReports.includes(reportId)) return 'after';
    return null;
  };

  const [showAutocomplete, setShowAutocomplete] = useState(false);

  let navigate = useNavigate();

  const [PrakritiCollection, setPrakritiCollection] = useState([]);

  const { Vikriti_data, isVikriti_dataloading, getVikritidata } =
    VikritiStore();

  const filteredVikritiData = Vikriti_data?.filter(
    (item) => item?.sub_category?.toLowerCase() !== "na"
  );

  const [open, setOpen] = useState(false);

  const [vScore, setVScore] = useState(0);

  const [pScore, setPScore] = useState(0);

  const [kScore, setKScore] = useState(0);

  const [VPK, setVPK] = useState("");

  const [diagnosisData, setDiagnosisData] = useState(null);

  // Analytics and Expansion State
  const [dpExpanded, setDpExpanded] = useState(true);
  const [bcExpanded, setBcExpanded] = useState(true);
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [analyticsType, setAnalyticsType] = useState('dp'); // 'dp' or 'bc'

  const openAnalytics = (type) => {
    setAnalyticsType(type);
    setAnalyticsModalOpen(true);
  };


  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const [open3, setOpen3] = useState(false);

  const handleClose3 = () => {
    setOpen3(false);
  };

  const handleOpen3 = () => {
    setOpen3(true);
  };

  const [isFirstClick, setIsFirstClick] = useState(true);

  const [open4, setOpen4] = useState(false);

  const [open5, setOpen5] = useState(false);

  const handleClose4 = () => {
    setOpen4(false);
  };

  const handleOpen4 = () => {
    setOpen4(true);
  };

  const handleClose5 = () => {
    setOpen5(false);
  };

  const handleOpen5 = () => {
    setOpen5(true);
  };

  const [loadingReport, setLoadingReport] = useState(false);
  const [generatingQuickReport, setGeneratingQuickReport] = useState(false);

  const handleGenerateQuickReport = async () => {
    try {
      if (!user?.Patient_details?.UID) return;
      setGeneratingQuickReport(true);

      const res = await kioskModel.post("/api/v1/diabetes/getIntegrativeDiabetesQuickReport", {
        UID: user?.Patient_details?.UID,
        environment: "prod"
      });

      if (res.data && res.data.data) {
        window.open(res.data.data, "_blank");
      }
    } catch (err) {
      console.error("Error generating quick report:", err);
    } finally {
      setGeneratingQuickReport(false);
    }
  };

  const [reportUrl, setReportUrl] = useState(null);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [openCompareReportDialog, setOpenCompareReportDialog] = useState(false);
  const [viewReportDialogOpen, setViewReportDialogOpen] = useState(false);

  let { setSelectedPatientdetails, setSelectedAppointemntdetails, resetPrescriptionState
  } =
    usePrescriptionStore();

  let [user, setuser] = useState();

  let [vikritidata, setvikritidata] = useState();

  const [searchTerm, setSearchTerm] = useState("");
  const [dietRecommendations, setDietRecommendations] = useState(null);
  const [lifestylePractices, setLifestylePractices] = useState(null);
  const [isDietLoading, setIsDietLoading] = useState(false);
  const [isLifestyleLoading, setIsLifestyleLoading] = useState(false);
  const [isVikritiReasonLoading, setIsVikritiReasonLoading] = useState(false);
  const [vikritiReason, setVikritiReason] = useState(null);
  const [hasHeartSessionData, setHasHeartSessionData] = useState(false);
  const [hasFacialLandmarkData, setHasFacialLandmarkData] = useState(false);
  const [facialData, setFacialData] = useState(null);



  const fetchVikritiReason = async (uid) => {
    try {
      setIsVikritiReasonLoading(true);
      const baseUrl = process.env.REACT_APP_KIOSK_URL || "http://192.168.31.123:8000/";
      const endpoint = baseUrl.endsWith('/') ? "api/v1/generateClinicalVikritiSuggestion" : "/api/v1/generateClinicalVikritiSuggestion";
      const res = await axios.post(`${baseUrl}${endpoint}`, { UID: uid });
      if (res.status === 200) {
        setVikritiReason(res.data.suggestion || res.data.reason || res.data.data?.reason || "Reason generated");
        // Re-fetch patient profile to get updated vikriti scores for the graph
        getPatientProfile();
      }
    } catch (err) {
      console.error("Error fetching vikriti reason:", err);
    } finally {
      setIsVikritiReasonLoading(false);
    }
  };

  const fetchDietaryRecommendation = async (uid) => {
    try {
      setIsDietLoading(true);
      const baseUrl = process.env.REACT_APP_KIOSK_URL || "http://192.168.31.123:8000/";
      const endpoint = baseUrl.endsWith('/') ? "api/v1/getDietaryRecommendation" : "/api/v1/getDietaryRecommendation";
      const res = await axios.post(`${baseUrl}${endpoint}`, { UID: uid });
      if (res.status === 200) {
        setDietRecommendations(res.data);
      }
    } catch (err) {
      console.error("Error fetching dietary recommendations:", err);
    } finally {
      setIsDietLoading(false);
    }
  };

  const fetchLifestylePractices = async (uid) => {
    try {
      setIsLifestyleLoading(true);
      const baseUrl = process.env.REACT_APP_KIOSK_URL || "http://192.168.31.123:8000/";
      const endpoint = baseUrl.endsWith('/') ? "api/v1/getLifestyleRecommendation" : "/api/v1/getLifestyleRecommendation";
      const res = await axios.post(`${baseUrl}${endpoint}`, { UID: uid });
      if (res.status === 200 && res.data?.data) {
        setLifestylePractices(res.data.data.LifestylePractices);
      }
    } catch (err) {
      console.error("Error fetching lifestyle practices:", err);
    } finally {
      setIsLifestyleLoading(false);
    }
  };

  const triggerAIRecommendations = async (uid) => {
    try {
      const baseUrl = process.env.REACT_APP_KIOSK_URL || "http://192.168.31.123:8000/";
      const dietEndpoint = baseUrl.endsWith('/') ? "api/v1/generateDietRecommendation" : "/api/v1/generateDietRecommendation";
      const lifestyleEndpoint = baseUrl.endsWith('/') ? "api/v1/generateLifestyleRecommendation" : "/api/v1/generateLifestyleRecommendation";

      await Promise.all([
        axios.post(`${baseUrl}${dietEndpoint}`, { UID: uid }).catch(err => console.error("Error generating diet:", err)),
        axios.post(`${baseUrl}${lifestyleEndpoint}`, { UID: uid }).catch(err => console.error("Error generating lifestyle:", err))
      ]);

      fetchDietaryRecommendation(uid);
      fetchLifestylePractices(uid);
    } catch (err) {
      console.error("Error triggering AI recommendations:", err);
    }
  };


  const checkHeartSessionData = async (uid) => {
    try {
      const res = await kioskModel.get(`api/v1/getClinicalHeartSessionsByPatientId/${uid}`);
      if (res.status === 200 && res.data && res.data.length > 0) {
        setHasHeartSessionData(true);
      }
    } catch (err) {
      console.error("Error checking heart session data:", err);
    }
  };

  const checkFacialLandmarkData = async (uid) => {
    try {
      const res = await kioskModel.get(`api/v1/getClinicalFacialLandmark/${uid}`);
      if (res.status === 200 && res.data && res.data.data) {
        setHasFacialLandmarkData(true);
        setFacialData(res.data.data);
      }
    } catch (err) {
      console.error("Error checking facial landmark data:", err);
    }
  };

  const renderTable = (type, data, label) => {
    if (!data || data.length === 0) return null;
    return (
      <table className="w-full mt-2 border-collapse border border-gray-200">
        <thead>
          <tr className="bg-[var(--themeSecondaryColor, #efefe7)]">
            <th className="border border-gray-200 p-2 font-medium text-[color:var(--themePrimaryColor,#312110)] font-['Poppins'] text-sm">Name</th>
            <th className="border border-gray-200 p-2 font-medium text-[color:var(--themePrimaryColor,#312110)] font-['Poppins'] text-sm">{label}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index}>
              <td className="border border-gray-200 p-2 text-left text-[var(--themePrimaryColor, #3B362A)] font-medium text-sm">
                <div className="flex flex-row items-center gap-2 justify-center">
                  {item.imageLink && (
                    <img src={item.imageLink} alt={item.asana || item.practice || item.name} className="w-8 h-8 object-contain" />
                  )}
                  <span>{item.asana || item.practice || item.name}</span>
                </div>
              </td>
              <td className="border border-gray-200 p-2 text-center text-[var(--themePrimaryColor, #3B362A)] font-medium text-sm">
                {item.reps || item.duration || item.count}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const symptomValue = event.target.value.trim();
      const existingSymptom = filteredVikritiData?.find(
        (item) => item.symptoms.toLowerCase() === symptomValue.toLowerCase()
      );

      if (
        symptomValue &&
        !user?.Vikriti_details?.vikritiList?.some(
          (item) => item.symptoms === symptomValue
        )
      ) {
        let newSymptomObj;

        if (existingSymptom) {
          newSymptomObj = {
            ...existingSymptom,
            sub_category: existingSymptom.sub_category || "na",
            category: existingSymptom.category || "na",
            dhatu_increase: existingSymptom.dhatu_increase || "na",
            dhatu_decrease: existingSymptom.dhatu_decrease || "na",
            dosha_increase: existingSymptom.dosha_increase || "na",
            dosha_decrease: existingSymptom.dosha_decrease || "na",
            mala_increase: existingSymptom.mala_increase || "na",
            mala_decrease: existingSymptom.mala_decrease || "na",
          };

          setuser((prevState) => ({
            ...prevState,
            Vikriti_details: {
              ...prevState?.Vikriti_details,
              vikritiList: [
                ...(prevState?.Vikriti_details?.vikritiList || []),
                newSymptomObj,
              ],
            },
          }));
        } else {
          newSymptomObj = {
            dosha_decrease: "na",
            symptoms: symptomValue,
            sub_category: "na",
            category: "na",
            dhatu_increase: "na",
            dosha_increase: "na",
            id: Math.random().toString(36).substr(2, 9),
            mala_increase: "na",
            mala_decrease: "na",
            dhatu_decrease: "na",
          };

          setuser((prevState) => ({
            ...prevState,
            Vikriti_details: {
              ...prevState?.Vikriti_details,
              vikritiList: [
                ...prevState?.Vikriti_details?.vikritiList,
                newSymptomObj,
              ],
            },
          }));
        }

        setInputValue("");
      }
    }
  };

  // const handleAutocompleteChange = (event, value) => {
  //     if (value != null) {
  //         const symptomValue = value?.trim();
  //         const existingSymptom = filteredVikritiData?.find(item => item.symptoms.toLowerCase() === symptomValue.toLowerCase());

  //         if (symptomValue && !user?.Vikriti_details?.vikritiList?.some((item) => item.symptoms === symptomValue)) {
  //             let newSymptomObj;

  //             if (existingSymptom) {
  //                 newSymptomObj = {
  //                     ...existingSymptom,
  //                     sub_category: existingSymptom.sub_category || 'na',
  //                     category: existingSymptom.category || 'na',
  //                     dhatu_increase: existingSymptom.dhatu_increase || 'na',
  //                     dhatu_decrease: existingSymptom.dhatu_decrease || 'na',
  //                     dosha_increase: existingSymptom.dosha_increase || 'na',
  //                     dosha_decrease: existingSymptom.dosha_decrease || 'na',
  //                     mala_increase: existingSymptom.mala_increase || 'na',
  //                     mala_decrease: existingSymptom.mala_decrease || 'na'
  //                 };
  //                 setuser((prevState) => ({
  //                     ...prevState,
  //                     Vikriti_details: {
  //                         ...prevState?.Vikriti_details,
  //                         vikritiList: [...prevState?.Vikriti_details?.vikritiList, newSymptomObj]
  //                     }
  //                 }));
  //             } else {
  //                 newSymptomObj = {
  //                     dosha_decrease: "na",
  //                     symptoms: symptomValue,
  //                     sub_category: "na",
  //                     category: "na",
  //                     dhatu_increase: "na",
  //                     dosha_increase: "na",
  //                     id: Math.random().toString(36).substr(2, 9),
  //                     mala_increase: "na",
  //                     mala_decrease: "na",
  //                     dhatu_decrease: "na"
  //                 };

  //                 setuser((prevState) => ({
  //                     ...prevState,
  //                     Vikriti_details: {
  //                         ...prevState?.Vikriti_details,
  //                         vikritiList: [...prevState?.Vikriti_details?.vikritiList, newSymptomObj]
  //                     }
  //                 }));
  //             }

  //             setInputValue('');
  //         }
  //     }
  // };

  function findVPKResult(arrayList) {
    try {
      const [vRaw, pRaw, kRaw] = arrayList;

      // Check for valid numeric input
      const isValid = (val) => typeof val === "number" && !isNaN(val);

      if (![vRaw, pRaw, kRaw].every(isValid)) return "";

      const datamap = {
        V: vRaw,
        P: pRaw,
        K: kRaw,
      };

      const datamap1 = {
        [vRaw]: "V",
        [pRaw]: "P",
        [kRaw]: "K",
      };

      const sorted = [vRaw, pRaw, kRaw].sort((a, b) => b - a); // descending
      const v = sorted[0];
      const k = sorted[2];
      const p = vRaw + pRaw + kRaw - v - k;

      if (v - p > 20) {
        return datamap1[v];
      } else if (v - p <= 20 && p - k > 5) {
        if (v === p) {
          const dominant = Object.entries(datamap)
            .filter(([key, value]) => value === v)
            .map(([key]) => key)
            .join("");
          return dominant;
        } else {
          return datamap1[v] + datamap1[p];
        }
      } else {
        return datamap1[v] + datamap1[p] + datamap1[k];
      }
    } catch (error) {
      console.error("Error in findVPKResult:", error);
      return "";
    }
  }


  const [newEmail, setNewEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const handleAddEmail = async () => {
    if (!newEmail) return;
    try {
      setSavingEmail(true);
      let res = await kioskModel.put("UpdateKioskProfile", {
        id,
        Patient_details: {
          email: newEmail
        }
      });
      if (res.status == 200) {
        getPatientProfile();
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSavingEmail(false);
    }
  };

  const getPatientProfile = async () => {
    console.log("getPatientProfile called with id:", id);
    try {
      setLoading(true);
      let res = await kioskModel.post("getClinicalPatientProfile", {
        id,
      });
      console.log("getClinicalPatientProfile response:", res);
      if (res.status == 200) {
        const data = res.data;
        if (Array.isArray(data.Prakriti_details)) {
          data.Prakriti_details = data.Prakriti_details[0];
        }
        if (Array.isArray(data.Vikriti_details)) {
          data.Vikriti_details = data.Vikriti_details[0];
        }
        // console.log("Body Composition Data (Fetched):", data?.body_composition);
        setuser(data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getVikritiData = async () => {
    try {
      let res = await kioskModel.get("fetchVikritidata", {
        id,
      });
      if (res.status == 200) {
        setvikritidata(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getWeightValue = () => {
    let rawWeight = null;
    let unit = 'kg';

    if (user?.Patient_details?.weight && user.Patient_details.weight !== "---") {
      rawWeight = user.Patient_details.weight;
    } else {
      const allBc = (Array.isArray(user?.body_composition) ? user?.body_composition : (user?.body_composition ? [user?.body_composition] : []))
        .filter(Boolean)
        .sort((a, b) => new Date(b.measuredAt || 0) - new Date(a.measuredAt || 0));

      if (allBc[0]?.weight?.value != null) {
        rawWeight = allBc[0].weight.value;
        unit = allBc[0].weight.unit || 'kg';
      }
    }

    if (rawWeight != null) {
      const parsed = parseFloat(rawWeight);
      if (!isNaN(parsed)) {
        const rounded = Math.round(parsed * 10) / 10;
        const hasUnit = typeof rawWeight === 'string' && /[a-zA-Z]/.test(rawWeight);
        if (hasUnit) {
          return rawWeight.replace(parsed.toString(), rounded.toString());
        }
        return `${rounded} ${unit}`;
      }
      return rawWeight;
    }
    return "NA";
  };

  const getHeightValue = () => {
    if (user?.Patient_details?.height && user.Patient_details.height !== "---") {
      return user.Patient_details.height;
    }
    const allBc = (Array.isArray(user?.body_composition) ? user?.body_composition : (user?.body_composition ? [user?.body_composition] : []))
      .filter(Boolean)
      .sort((a, b) => new Date(b.measuredAt || 0) - new Date(a.measuredAt || 0));

    const latestBc = allBc[0];
    if (latestBc?.bmi && latestBc?.weight?.value) {
      const hMeters = Math.sqrt(latestBc.weight.value / latestBc.bmi);
      if (!isNaN(hMeters) && isFinite(hMeters)) {
        return `${Math.round(hMeters * 100)} cm`;
      }
    }
    return "NA";
  };

  const symptomsList = vikritidata
    ?.map((data) => data.symptoms)
    .filter((symptom) => symptom);

  useEffect(() => {
    if (id) {
      console.log("useEffect triggered for patient profile fetch");
      getPatientProfile();
      getVikritiData();
    }
  }, [id]);

  useEffect(() => {
    if (user?.Patient_details?.UID) {
      getReports(user?.Patient_details?.UID, id);
      fetchDietaryRecommendation(user?.Patient_details?.UID);
      fetchLifestylePractices(user?.Patient_details?.UID);
      checkHeartSessionData(user?.Patient_details?.UID);
      checkFacialLandmarkData(user?.Patient_details?.UID);
    }
  }, [user?.Patient_details?.UID]);

  useEffect(() => {
    const reason = user?.Vikriti_details?.reason;
    const uid = user?.Patient_details?.UID;
    if (uid && (!reason || reason === "None") && !isVikritiReasonLoading && !vikritiReason) {
      fetchVikritiReason(uid);
    }
  }, [user?.Vikriti_details?.reason, user?.Patient_details?.UID]);


  useEffect(() => {
    const element = document.querySelector('.home-content');
    if (element) {
      element.scrollTo(0, 0);
    }
  }, [id]);

  function calculateDoshaValues(vikritiResponse = {}) {
    const { vata, pitta, kapha, Result } = vikritiResponse || {};

    const uniqueCategories = new Set();

    if (
      typeof vata === 'number' &&
      typeof pitta === 'number' &&
      typeof kapha === 'number' &&
      typeof Result === 'string'
    ) {
      return {
        doshaValues: { v: vata, p: pitta, k: kapha },
        uniqueCategories: [],
      };
    }

    if (!vikritiResponse?.vikritiList || vikritiResponse?.vikritiList.length === 0) {
      const v = vikritiResponse?.vataAssScore || vikritiResponse?.vata || 0;
      const p = vikritiResponse?.pittaAssScore || vikritiResponse?.pitta || 0;
      const k = vikritiResponse?.kaphaAssScore || vikritiResponse?.kapha || 0;

      return {
        doshaValues: { v, p, k },
        uniqueCategories: [],
      };
    }

    let v = 0, p = 0, k = 0;

    vikritiResponse.vikritiList.forEach((vikriti) => {
      vikriti?.dosha_increase?.split(',')?.forEach((dosha) => {
        switch (dosha.trim().toLowerCase()) {
          case 'v': v++; break;
          case 'p': p++; break;
          case 'k': k++; break;
        }
      });

      vikriti?.dosha_decrease?.split(',')?.forEach((dosha) => {
        switch (dosha.trim().toLowerCase()) {
          case 'v': v--; break;
          case 'p': p--; break;
          case 'k': k--; break;
        }
      });

      if (vikriti?.category) uniqueCategories.add(vikriti.category);
    });

    return {
      doshaValues: { v, p, k },
    };
  }

  function calculatePercentages(vikritiResponse = {}) {
    // Extract all possible score fields with defaults
    const {
      vataAssScore,
      pittaAssScore,
      kaphaAssScore,
      vataPulseScore = 0,
      pittaPulseScore = 0,
      kaphaPulseScore = 0,
      vata: vataDirect,
      pitta: pittaDirect,
      kapha: kaphaDirect,
      Result = ''
    } = vikritiResponse || {};

    // Calculate total scores for each dosha
    // Prioritize specific fields if available
    const vataTotal = (vataAssScore ?? vataDirect ?? 0) + vataPulseScore;
    const pittaTotal = (pittaAssScore ?? pittaDirect ?? 0) + pittaPulseScore;
    const kaphaTotal = (kaphaAssScore ?? kaphaDirect ?? 0) + kaphaPulseScore;
    const totalScore = vataTotal + pittaTotal + kaphaTotal;

    // If all scores are 0, return 0 for all
    if (totalScore === 0) {
      return {
        vata: 0,
        pitta: 0,
        kapha: 0,
        Result: Result || '',
      };
    }

    // Calculate percentages
    const calculatePercentage = (score) => Math.round((score / totalScore) * 100);

    return {
      doshaPercentages: {
        v: calculatePercentage(vataTotal),
        p: calculatePercentage(pittaTotal),
        k: calculatePercentage(kaphaTotal),
      },
      // uniqueCategories: Array.from(uniqueCategories),
      Result: Result || '',
    };
  }


  useEffect(() => {
    getPatientProfile();
    getVikritiData();

  }, []);



  const [editedData, setEditedData] = useState(
    user?.Vikriti_details?.vikritiList || []
  );

  const [openAi, setOpenAi] = useState(false);

  const handleOpenAi = () => {
    setOpenAi(true);
    handleAyurvedaDiagnosisAISuggestion();
  };

  const vikritiscore = {
    vata: user?.Vikriti_details?.vata,
    pitta: user?.Vikriti_details?.pitta,
    kapha: user?.Vikriti_details?.kapha,
  }
  const vikritiresultData = calculatePercentages(user?.Vikriti_details);

  const findDominantDosha = (prakritiState) => {
    if (prakritiState) {
      if (
        prakritiState?.vata >=
        Math.max(prakritiState?.pitta, prakritiState?.kapha)
      ) {
        return ["Vata", "Dominant", `${prakritiState?.vata?.toFixed(0)}%`];
      } else if (
        prakritiState.pitta >=
        Math.max(prakritiState?.vata, prakritiState?.kapha)
      ) {
        return ["Pitta", "Dominant", `${prakritiState?.pitta?.toFixed(0)}%`];
      } else {
        return ["Kapha", "Dominant", `${prakritiState?.kapha?.toFixed(0)}%`];
      }
    }
    return ["", "Dominant", 0];
  };

  const [editQuestionIndex, setEditQuestionIndex] = useState(null);

  const handleEditAnswer = (questionIndex) => {
    setEditQuestionIndex(questionIndex);
  };

  const handleAnswerChange = (questionIndex, newAnswerIndex) => {
    if (!user?.Prakriti_details?.PrakritiCollection) return;

    const updatedPrakritiDetails = user.Prakriti_details.PrakritiCollection.map(
      (q, qIndex) => {
        if (qIndex === questionIndex) {
          return {
            ...q,
            Answers: q.Answers.map((ans, ansIndex) => ({
              ...ans,
              isSelected: ansIndex === newAnswerIndex,
            })),
          };
        }
        return q; // Return other questions as-is
      }
    );

    // Update the user state
    setuser({
      ...user,
      Prakriti_details: {
        ...user.Prakriti_details,
        PrakritiCollection: updatedPrakritiDetails,
      },
    });

    // Exit edit mode
    setEditQuestionIndex(null);
  };

  const handleKeyPress = (event) => {
    const charCode = event?.charCode;
    if (event?.keyCode !== 8 && (charCode < 48 || charCode > 57)) {
      event?.preventDefault();
    }
  };

  const handleSaveChanges = async () => {
    try {
      const promises =
        PrakritiCollection?.map(async (question) => {
          const selectedAnswer = question?.Answers?.find(
            (answer) => answer.isSelected
          );
          if (selectedAnswer) {
            return updateScores(selectedAnswer);
          }
        }) || [];

      await Promise.all(promises.filter(Boolean));

      handleSubmitResult();
    } catch (error) {
      console.error("handleSaveChanges: Error occurred", error);
    }
  };

  const handleClick = async () => {
    if (isFirstClick) {
      await handleSaveChanges();
      await handleSaveChanges();
      setIsFirstClick(false);
    } else {
      await handleSaveChanges();
    }
  };

  const updateScores = (newAnswer) => {
    if (newAnswer && newAnswer?.score && newAnswer?.vpk) {
      const newScore = parseFloat(newAnswer.score) || 0;

      switch (newAnswer.vpk.toLowerCase()) {
        case "v":
          setVScore((prevScore) => prevScore + newScore);
          break;
        case "p":
          setPScore((prevScore) => prevScore + newScore);
          break;
        case "k":
          setKScore((prevScore) => prevScore + newScore);
          break;
      }
    }
  };


  const handleSaveAISuggestion = async (diagnosisData) => {
    try {
      if (!user || !user.Vikriti_details) {
        console.log("User or Vikriti_details not found");
        return;
      }

      let { vataAssScore, pittaAssScore, kaphaAssScore } = user.Vikriti_details;

      if (!diagnosisData || typeof diagnosisData !== "object") {
        console.error("Invalid diagnosisData format");
        return;
      }

      if (diagnosisData.increased_dosha?.dosha) {
        switch (diagnosisData.increased_dosha.dosha.toLowerCase()) {
          case "vata": vataAssScore += 5; break;
          case "pitta": pittaAssScore += 5; break;
          case "kapha": kaphaAssScore += 5; break;
        }
      }

      if (diagnosisData.decreased_dosha?.dosha) {
        switch (diagnosisData.decreased_dosha.dosha.toLowerCase()) {
          case "vata": vataAssScore = Math.max(vataAssScore - 5, 0); break;
          case "pitta": pittaAssScore = Math.max(pittaAssScore - 5, 0); break;
          case "kapha": kaphaAssScore = Math.max(kaphaAssScore - 5, 0); break;
        }
      }

      const total = vataAssScore + pittaAssScore + kaphaAssScore;

      if (total > 0) {
        vataAssScore = (vataAssScore / total) * 100;
        pittaAssScore = (pittaAssScore / total) * 100;
        kaphaAssScore = (kaphaAssScore / total) * 100;
      }

      const doshaOrder = [
        { dosha: "V", score: vataAssScore },
        { dosha: "P", score: pittaAssScore },
        { dosha: "K", score: kaphaAssScore },
      ].sort((a, b) => b.score - a.score)
        .map(d => d.dosha)
        .join("");

      setuser(prevUser => ({
        ...prevUser,
        Vikriti_details: {
          ...prevUser.Vikriti_details,
          vataAssScore: Math.round(vataAssScore),
          pittaAssScore: Math.round(pittaAssScore),
          kaphaAssScore: Math.round(kaphaAssScore),
          Result: doshaOrder,
        }
      }));

      if (diagnosisData) setDiagnosisData(null);


      console.log({
        vataAssScore: Math.round(vataAssScore),
        pittaAssScore: Math.round(pittaAssScore),
        kaphaAssScore: Math.round(kaphaAssScore),
        Result: doshaOrder,
      });

      setOpenAi(false)

    } catch (err) {
      console.error("Error saving AI suggestion:", err);
    }
  };

  function PrakriticalculatePercentages(vScore, pScore, kScore) {
    const totalScore = Math.abs(vScore) + Math.abs(pScore) + Math.abs(kScore);

    if (totalScore > 0) {
      const vScorePercentage = (vScore / totalScore) * 100;
      const pScorePercentage = (pScore / totalScore) * 100;
      const kScorePercentage = (kScore / totalScore) * 100;

      return {
        vScorePercentage: parseFloat(vScorePercentage?.toFixed(2)),
        pScorePercentage: parseFloat(pScorePercentage?.toFixed(2)),
        kScorePercentage: parseFloat(kScorePercentage?.toFixed(2)),
      };
    } else {
      return {
        vScorePercentage: 0.0,
        pScorePercentage: 0.0,
        kScorePercentage: 0.0,
      };
    }
  }

  async function findVPK(arrayList) {
    try {
      const datamap = { V: arrayList[0], P: arrayList[1], K: arrayList[2] };

      const datamap1 = {
        [arrayList[0]]: "V",
        [arrayList[1]]: "P",
        [arrayList[2]]: "K",
      };

      const sortedList = [...arrayList].sort((a, b) => a - b);
      const [k, p, v] = sortedList;

      if (v - p > 20) return datamap1[v];
      if (v - p <= 20 && p - k > 5) {
        return v === p
          ? Object.entries(datamap)
            .filter(([_, val]) => val === v)
            .map(([key]) => key)
            .join("")
          : `${datamap1[v]}${datamap1[p]}`;
      }
      return `${datamap1[v]}${datamap1[p]}${datamap1[k]}`;
    } catch (error) {
      console.error("Error in findVPK:", error);
      throw error;
    }
  }

  const handleSubmitResult = async () => {
    try {
      const percentages = PrakriticalculatePercentages(vScore, pScore, kScore);

      const VPk_result = await findVPK([
        percentages?.vScorePercentage,
        percentages?.pScorePercentage,
        percentages?.kScorePercentage,
      ]);

      setVPK(VPk_result);

      await updatePrakritiResult(
        percentages?.vScorePercentage,
        percentages?.pScorePercentage,
        percentages?.kScorePercentage,
        VPk_result
      );
    } catch (error) {
      console.error("Error in handleSubmitResult:", error);
    }
  };

  const updatePrakritiResult = async (vata, pitta, kapha, VPk_result) => {
    try {

      setLoading(true);
      const res = await kioskModel.post("/UpdateClinicalPrakriti", {
        UID: user?.Patient_details?.UID,
        result: VPk_result,
        date: Date.now().toString(),
        vata: Math.round(vata),
        pitta: Math.round(pitta),
        kapha: Math.round(kapha),
        rawVata: vScore,
        rawKapha: kScore,
        rawPitta: pScore,
        id: user?.Patient_details?.UID,
        PrakritiCollection: user.Prakriti_details.PrakritiCollection,
      });

      if (res.status === 200) {
        handleClose();
        getPatientProfile();
        await triggerAIRecommendations(user?.Patient_details?.UID);
      }
    } catch (err) {
      console.error(
        "Error updating Prakriti result:",
        err?.response?.data || err
      );
    } finally {
      setLoading(false);
    }
  };

  const EditPrakritiResult = async () => {
    try {
      setLoading(true);

      const resultValue = findVPKResult([
        user?.Prakriti_details?.vata,
        user?.Prakriti_details?.pitta,
        user?.Prakriti_details?.kapha,
      ]);

      const payload = {
        UID: user?.Patient_details?.UID,
        ...user?.Prakriti_details,
        result: resultValue,
      };

      const res = await kioskModel.post("/UpdateClinicalPrakriti", payload);

      if (res.status === 200) {
        handleClose4();
        getPatientProfile();
        await triggerAIRecommendations(user?.Patient_details?.UID);
      }
    } catch (err) {
      console.error("Error updating Prakriti result:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  // const handleAdd = () => {
  //   const newRow = {
  //     category: "",
  //     sub_category: "",
  //     symptoms: "",
  //     dosha_increase: "",
  //     dosha_decrease: "",
  //   };
  //   setEditedData([...editedData, newRow]); // Appends a row at the bottom
  // };

  // const handleRemove = (index) => {
  //   setEditedData((prevData) => prevData.filter((_, i) => i !== index));
  // };

  // const handleEditChange = (index, field, value) => {
  //   setEditedData((prevData) =>
  //     prevData.map((item, i) =>
  //       i === index ? { ...item, [field]: value } : item
  //     )
  //   );
  // };

  // const UpdateVkikriti = async () => {
  //   try {
  //     setLoading(true);
  //     let res = await kioskModel.post("/UpdateVikriti", {
  //       ...user?.Vikriti_details,
  //     });

  //     if (res?.status === 200 && res?.data) {
  //       handleClose5();
  //       handleClose3();
  //       getPatientProfile();
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   }finally{
  //     setLoading(false);
  //   }
  // };


  // const groupedData = user?.Vikriti_details?.vikritiList?.reduce((acc, item) => {
  //     if (!acc[item.category]) {
  //         acc[item.category] = [];
  //     }
  //     acc[item.category].push(item);
  //     return acc;
  // }, {});

  //dummy data

  // const data = {
  //   Diseases: [
  //     "Diabetes",
  //     "Hypertension",
  //     "Asthma",
  //     "Arthritis",
  //     "Heart Disease",
  //     "Migraine",
  //     "Thyroid Disorder",
  //     "Obesity",
  //   ],
  // };

  const handleAdd = () => {

    const newRow = {
      category: "",
      sub_category: "",
      symptoms: "",
      dosha_increase: "",
      dosha_decrease: "",
    };

    setuser((prevUser) => ({
      ...prevUser,

      Vikriti_details: {
        ...prevUser?.Vikriti_details,
        vikritiList: [...(prevUser?.Vikriti_details?.vikritiList || []), newRow],
      },

    }));


  };

  const handleRemove = (index) => {
    setuser((prevUser) => ({
      ...prevUser,
      Vikriti_details: {
        ...prevUser?.Vikriti_details,
        vikritiList: prevUser?.Vikriti_details?.vikritiList.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const handleEditChange = (index, field, value) => {
    setuser((prevUser) => ({
      ...prevUser,
      Vikriti_details: {
        ...prevUser?.Vikriti_details,
        vikritiList: prevUser?.Vikriti_details?.vikritiList.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };


  function calculateVikritiDoshaValues(vikritiResponse) {
    let v = 0;
    let p = 0;
    let k = 0;

    const uniqueCategories = new Set();

    vikritiResponse?.forEach(vikriti => {
      vikriti?.dosha_increase?.split(",")?.forEach(dosha => {
        switch (dosha.trim().toLowerCase()) {
          case 'v':
            v++;
            break;
          case 'p':
            p++;
            break;
          case 'k':
            k++;
            break;
        }
      });

      vikriti?.dosha_decrease?.split(",")?.forEach(dosha => {
        switch (dosha.trim().toLowerCase()) {
          case 'v':
            v--;
            break;
          case 'p':
            p--;
            break;
          case 'k':
            k--;
            break;
        }
      });

      uniqueCategories.add(vikriti.category);
    });


    const totalDoshas = vikritiResponse.length;


    const vPercent = (v / totalDoshas) * 100;
    const pPercent = (p / totalDoshas) * 100;
    const kPercent = (k / totalDoshas) * 100;

    return { vataAssScore: v, pittaAssScore: p, kaphaAssScore: k }
  }

  const UpdateVkikriti = async () => {
    try {

      setLoading(true);

      let res = await kioskModel.post("/UpdateVikriti", {
        UID: user?.Patient_details?.UID,
        count: user?.Vikriti_details.vikritiList?.length,
        date: Date?.now()?.toString(),
        vataPulseScore: 0,
        pittaPulseScore: 0,
        kaphaPulseScore: 0,
        vikritiList: user?.Vikriti_details.vikritiList,
      });

      if (res?.status === 200 && res?.data) {
        handleClose5();
        handleClose3();
        getPatientProfile();
        await triggerAIRecommendations(user?.Patient_details?.UID);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVikritiSavechanges = async () => {
    UpdateVkikriti()
  }

  const handleAyurvedaDiagnosisAISuggestion = async () => {
    try {
      setLoading(true);

      const details = {
        Prakriti: user?.Prakriti_details?.result || "",
        Vikriti: user?.Vikriti_details?.result || "",
        Symptoms:
          Array.isArray(user?.Vikriti_details?.vikritiList) && user?.Vikriti_details?.vikritiList.length
            ? user?.Vikriti_details?.vikritiList.map((e) => e?.symptoms).join(", ")
            : "",
        Gender: selectedPatientdetails?.gender || "",
        Height: selectedPatientdetails?.height || "",
        Weight: selectedPatientdetails?.weight || "",
      };

      const querySuggestion = `
        Given a person whose prakriti is ${details?.Prakriti}, and has symptoms like 
        ${details?.Symptoms}. 
        Please determine the vikriti for this individual mentioning the increased and decreased dosha (vata, pitta, kapha). 
        Provide a concise reasoning in 2 to 3 sentences. Now, return the output in the following JSON format: 
        {
          "increased_dosha": { "dosha": "", "reason": "sample reason." },
          "decreased_dosha": { "dosha": "", "reason": "sample reason." }
        }
      `;

      const res = await shopModel.post("/checkQuery", {
        query: querySuggestion,
      });

      if (res.status === 200) {
        let cleanData = res.data.trim();
        if (cleanData.startsWith("```json")) {
          cleanData = cleanData.replace(/```json|```/g, "").trim();
        }
        const parsedData = JSON.parse(cleanData);

        setDiagnosisData(parsedData);
        console.log("API Response:", parsedData);

        setLoading(false);
      } else {
        console.log("Unexpected API response:", res);
      }

    } catch (err) {
      console.error("Error fetching AI response:", err);
    } finally {
      setLoading(false);
    }
  };

  const SendReportsToWBHKiosk = async () => {
    try {
      setLoadingReport(true);
      const {
        email,
        phone,
        UID,
        firstname,
        lastname,
        gender,
        age
      } = user?.Patient_details || {};

      const {
        systolic,
        diastolic,
        spo2,
        pulse,
        bca_report,
        bca_link,
        rbs,
        handgrip,
      } = user?.digital_params || {};

      const payload = {
        email,
        phone,
        name: `${firstname} ${lastname}`,
        gender,
        age,
        systolic,
        diastolic,
        spo2,
        pulse,
        bca_report,
        bca_link,
        rbs,
        handgrip,
        Prakriti_details: user?.Prakriti_details,
        Vikriti_details: user?.Vikriti_details,
        Patient_details: user?.Patient_details,
        digital_params: user?.digital_params,
        body_composition: user?.body_composition,
        ayurvedarecommendation: '',
        packagetype: "Advanced",
        UID,
        environment: "prod",
      };

      const res = await kioskModel.post("/api/v1/diabetes/SendReportstoClinicalKiosk", payload);

      if (res.status === 200) {

        if (res.data && res.data.data) {
          setReportUrl(res.data.data);
          setOpenReportDialog(true);

          // Refresh the patient profile so the 'View Report' list gets updated with the new report
          getPatientProfile();
        }
      }
    } catch (err) {
      console.error("Error sending report:", err);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportUrl) return;

    try {
      // Fetch the file as a blob
      const response = await fetch(reportUrl);
      const blob = await response.blob();

      // Create a temporary URL
      const url = window.URL.createObjectURL(blob);

      // Create and trigger a download link
      const link = document.createElement("a");
      link.href = url;
      link.download = `Report_${user?.Patient_details?.firstname || "Patient"}.pdf`; // Custom filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading report:", error);
    }
  };

  const [symptom, setSymptomList] = useState([]);

  const [diseaseListNew, setDiseaseList] = useState([]);

  useEffect(() => {
    const fetchDiseaseAndSymptoms = async () => {
      try {
        const res = await kioskModel.get(`/api/v1/clinical/diabetes/user-answers/${user?.Patient_details?.UID}`);
        if (res?.data?.success) {
          const complicationsStr = res.data.complications || "";
          const symptomsStr = res.data.symptoms || "";

          const complicationsArray = complicationsStr
            .split(",")
            .map(item => item.trim())
            .filter(Boolean)
            .map((item, idx) => ({ id: idx, symptoms: item }));

          const symptomsArray = symptomsStr
            .split(",")
            .map(item => item.trim())
            .filter(Boolean)
            .map((item, idx) => ({ id: idx, symptoms: item }));

          setDiseaseList(complicationsArray);
          setSymptomList(symptomsArray);
        }
      } catch (err) {
        console.error("Error fetching disease and symptoms:", err);
      }
    };

    if (user?.Patient_details?.UID) {
      fetchDiseaseAndSymptoms();
    }
  }, [user?.Patient_details?.UID]);
  const [diabetesFollowup, setDiabetesFollowup] = useState(null);
  const [diabetesEditModalOpen, setDiabetesEditModalOpen] = useState(false);
  const [profileEditModalOpen, setProfileEditModalOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({
    firstname: "",
    gender: "",
    dob: "",
    weight: "",
    height: "",
    phone: "",
    waist: "",
    email: ""
  });

  const handleOpenProfileEdit = () => {
    setEditProfileForm({
      firstname: user?.Patient_details?.firstname || "",
      gender: user?.Patient_details?.gender || "",
      dob: user?.Patient_details?.dob || "",
      weight: user?.Patient_details?.weight && user?.Patient_details?.weight !== "---" ? user?.Patient_details?.weight : "",
      height: user?.Patient_details?.height && user?.Patient_details?.height !== "---" ? user?.Patient_details?.height : "",
      phone: user?.Patient_details?.phone && user?.Patient_details?.phone !== "---" ? user?.Patient_details?.phone : "",
      waist: user?.Patient_details?.waist || "",
      email: user?.Patient_details?.email && user?.Patient_details?.email !== "---" ? user?.Patient_details?.email : ""
    });
    setProfileEditModalOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const payload = {
        id: user?.Patient_details?.UID || id,
        Patient_details: {
          firstname: editProfileForm.firstname,
          gender: editProfileForm.gender,
          dob: editProfileForm.dob,
          weight: editProfileForm.weight,
          height: editProfileForm.height,
          phone: editProfileForm.phone,
          waist: editProfileForm.waist,
          email: editProfileForm.email
        }
      };

      const res = await kioskModel.put("UpdateKioskProfile", payload);
      
      if (res.status === 200 || res.status === 201) {
        setProfileEditModalOpen(false);
        getPatientProfile();
      }
    } catch (err) {
      console.error("Error saving profile:", err);
    } finally {
      setSavingProfile(false);
    }
  };
  const [savingDiabetesFollowup, setSavingDiabetesFollowup] = useState(false);
  const [editDiabetesForm, setEditDiabetesForm] = useState({
    isDiabetic: "No",
    diabetesType: "",
    fastingGlucose: "",
    hba1c: ""
  });

  const fetchDiabetesFollowupData = async () => {
    if (!user?.Patient_details?.UID) return;
    try {
      const res = await kioskModel.get(`api/v1/clinical/diabetes-assessment/followup/${user?.Patient_details?.UID}`);
      if (res?.status === 200) {
        const list = res.data?.data || res.data || [];
        const sorted = Array.isArray(list)
          ? list.sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt))
          : [];
        if (sorted.length > 0) {
          setDiabetesFollowup(sorted[0]);
        }
      }
    } catch (err) {
      console.error("Error fetching diabetes followup:", err);
    }
  };

  useEffect(() => {
    fetchDiabetesFollowupData();
  }, [user?.Patient_details?.UID]);

  const handleOpenDiabetesEdit = () => {
    setEditDiabetesForm({
      isDiabetic: diabetesFollowup?.isDiabetic ? "Yes" : "No",
      diabetesType: diabetesFollowup?.diabetesType || "",
      fastingGlucose: diabetesFollowup?.fastingGlucose || "",
      hba1c: diabetesFollowup?.hba1c || ""
    });
    setDiabetesEditModalOpen(true);
  };

  const handleSaveDiabetesFollowup = async () => {
    try {
      setSavingDiabetesFollowup(true);
      const payload = {
        userId: user?.Patient_details?.UID,
        isDiabetic: editDiabetesForm.isDiabetic === "Yes",
        diabetesType: editDiabetesForm.isDiabetic === "Yes" ? editDiabetesForm.diabetesType : "",
        fastingGlucose: editDiabetesForm.isDiabetic === "Yes" && editDiabetesForm.fastingGlucose ? parseFloat(editDiabetesForm.fastingGlucose) : null,
        hba1c: editDiabetesForm.isDiabetic === "Yes" && editDiabetesForm.hba1c ? parseFloat(editDiabetesForm.hba1c) : null
      };

      const res = await kioskModel.post("api/v1/clinical/diabetes-assessment/followup", payload);
      
      if (res.status === 200 || res.status === 201) {
        setDiabetesEditModalOpen(false);
        fetchDiabetesFollowupData();
      }
    } catch (err) {
      console.error("Error saving diabetes followup:", err);
    } finally {
      setSavingDiabetesFollowup(false);
    }
  };


  let comparisonData = useMemo(() => {
    const isComparisonActive = selectedBeforeReports.length > 0 && selectedAfterReports.length > 0;
    if (!isComparisonActive) return { rows: [], reportDates: [] };

    // Combine all selected report IDs from both lists
    const allSelectedIds = [...new Set([...selectedBeforeReports, ...selectedAfterReports])];
    const selectedReports = (Reports || [])
      .filter(r => allSelectedIds.includes(r.id))
      .sort((a, b) => new Date(b.CreatedAt || 0) - new Date(a.CreatedAt || 0));

    // Filter to the 5 most recent unique days
    const uniqueDayReports = [];
    const seenDays = new Set();
    for (const r of selectedReports) {
      if (!r.CreatedAt) continue;
      const day = new Date(r.CreatedAt).toLocaleDateString('en-IN');
      if (!seenDays.has(day)) {
        uniqueDayReports.push(r);
        seenDays.add(day);
      }
      if (uniqueDayReports.length >= 5) break;
    }

    // reverse so earliest is first (left to right)
    const displayReports = [...uniqueDayReports].reverse();
    const reportDates = displayReports.map(r => ({
      id: r.id,
      dateStr: new Date(r.CreatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      year: new Date(r.CreatedAt).getFullYear()
    }));

    const paramMap = new Map();

    displayReports.forEach((reportGroup, rIdx) => {
      const json = cleanJson(reportGroup?.Reports?.[0]?.ai_analysis);
      if (!json) return;

      [...(json.abnormal_parameters || []), ...(json.normal_parameters || [])]
        .forEach(param => {
          const key = normalizeName(param.name);
          if (!paramMap.has(key)) {
            paramMap.set(key, {
              name: param.name,
              unit: param.unit,
              refRange: param.reference_range,
              values: new Array(displayReports.length).fill(null)
            });
          }
          const pData = paramMap.get(key);
          pData.values[rIdx] = {
            value: (param.value === 0 || param.value === '0' || !param.value) ? null : param.value,
            status: param.status
          };
          if (!pData.unit && param.unit) pData.unit = param.unit;
          if (!pData.refRange && param.reference_range) pData.refRange = param.reference_range;
        });
    });

    return {
      rows: [...paramMap.values()],
      reportDates
    };
  }, [Reports, selectedBeforeReports, selectedAfterReports]);


  return (
    <div className="Patient_Profile">
      {loading && <LoadingComponent loading={[teleConsultationModel, teleConsultationModel, shopModel]} />}
      <nav className="flex items-center p-2 mt-2 mx-2 border rounded-lg">
        <Link to="/Appointments" className="flex items-center cursor-pointer">
          <IconButton>
            <ChevronLeft />
          </IconButton>
          <p className="ml-2 text-lg font-medium">Appointments</p>
        </Link>
        <div style={{ marginLeft: "auto" }}>
          <Button
            variant="contained"
            onClick={handleGenerateQuickReport}
            disabled={generatingQuickReport}
            sx={{ backgroundColor: "var(--themePrimaryColor)", ":hover": { backgroundColor: "var(--themePrimaryColor)" }, whiteSpace: "nowrap" }}
          >
            {generatingQuickReport ? <CircularProgress size={24} style={{ color: 'white' }} /> : "Generate Quick Report"}
          </Button>
        </div>
      </nav>

      <div className="Patient-appointment-details flex flex-row items-center mt-5">
        <div
          className="Patient-card flex flex-row items-center"
          style={{ flex: 1, width: "100%" }}
        >
          {facialData && facialData.imageUrl ? (
            <div className="Patient-avatar" style={{ overflow: "hidden" }}>
              <img src={facialData.imageUrl} alt="Facial Analysis" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          ) : user?.Patient_details?.src && user?.Patient_details?.src != "" ? (
            <div className="Patient-avatar">
              <img src={user?.Patient_details?.src}></img>
            </div>
          ) : (
            <div className="Patient-avatar">
              <p>
                <p>
                  {user?.Patient_details?.firstname?.charAt(0)}{" "}
                  {user?.Patient_details?.lastname?.charAt(0)}
                </p>
              </p>
            </div>
          )}

          <div style={{ flexGrow: 1, width: "100%", paddingRight: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p className="patient_name text-start" style={{ margin: 0, marginLeft: "10px" }}>
                {user?.Patient_details?.firstname}{" "}
                {user?.Patient_details?.lastname}
              </p>
              <IconButton size="small" onClick={handleOpenProfileEdit} sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }}>
                <Edit fontSize="small" />
              </IconButton>
            </div>
            
            <Dialog open={profileEditModalOpen} onClose={() => setProfileEditModalOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, color: "#3B362A" }}>
                Edit Profile
              </DialogTitle>
              <DialogContent dividers>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", paddingTop: "8px" }}>
                  <TextField
                    label="First Name"
                    value={editProfileForm.firstname}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, firstname: e.target.value })}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      label="Gender"
                      value={editProfileForm.gender}
                      onChange={(e) => setEditProfileForm({ ...editProfileForm, gender: e.target.value })}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Age (Years)"
                    value={editProfileForm.dob}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, dob: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={editProfileForm.phone}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, phone: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Email"
                    value={editProfileForm.email}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, email: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Height"
                    value={editProfileForm.height}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, height: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Weight"
                    value={editProfileForm.weight}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, weight: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Waist"
                    value={editProfileForm.waist}
                    onChange={(e) => setEditProfileForm({ ...editProfileForm, waist: e.target.value })}
                    fullWidth
                  />
                </div>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setProfileEditModalOpen(false)} sx={{ color: '#64748b' }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveProfile} 
                  variant="contained" 
                  disabled={savingProfile}
                  sx={{ backgroundColor: "var(--themePrimaryColor, #DA6E0B)", "&:hover": { backgroundColor: "#B85C09" } }}
                >
                  {savingProfile ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
                </Button>
              </DialogActions>
            </Dialog>

            <div className="patient-info-container">
              {user?.Patient_details?.gender && user.Patient_details.gender !== "---" && (
                <div className="info-item">
                  <img
                    src={
                      user.Patient_details.gender === "Male"
                        ? "/icons/Male_icon.png"
                        : "/icons/female_icon.png"
                    }
                    alt="Gender Icon"
                  />
                  <p>{user.Patient_details.gender}</p>
                </div>
              )}

              {user?.Patient_details?.dob && user.Patient_details.dob !== "---" && (
                <div className="info-item">
                  <img src="/icons/age.png" alt="Age Icon" />
                  <p>
                    {`${user.Patient_details.dob} years`}
                  </p>
                </div>
              )}

              {getWeightValue() !== "NA" && (
                <div className="info-item">
                  <img src="/icons/weight.png" alt="Weight Icon" />
                  <p>
                    {getWeightValue()}
                  </p>
                </div>
              )}

              {getHeightValue() !== "NA" && (
                <div className="info-item">
                  <img src="/icons/height.png" alt="Height Icon" />
                  <p>
                    {getHeightValue()}
                  </p>
                </div>
              )}

              {user?.Patient_details?.phone && user.Patient_details.phone !== "---" && (
                <div className="info-item">
                  <img src="/icons/mobile_icon.png" alt="Phone Icon" />
                  <p>
                    {user.Patient_details.phone}
                  </p>
                </div>
              )}

              <div className="info-item" style={{ minWidth: "max-content" }}>
                <img src="/icons/mail_icon.png" alt="Email Icon" />
                {user?.Patient_details?.email && user.Patient_details.email !== "---" ? (
                  <p>{user.Patient_details.email}</p>
                ) : (
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <input
                      type="email"
                      placeholder="Enter Email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        fontSize: "12px",
                        outline: "none",
                        width: "140px"
                      }}
                    />
                    <button
                      onClick={handleAddEmail}
                      disabled={savingEmail}
                      style={{
                        padding: "4px 12px",
                        background: "#3B362A",
                        color: "white",
                        borderRadius: "4px",
                        cursor: "pointer",
                        border: "none",
                        fontSize: "12px",
                        opacity: savingEmail ? 0.7 : 1
                      }}
                    >
                      {savingEmail ? "Saving..." : "Add"}
                    </button>
                  </div>
                )}
              </div>

              {user?.Patient_details?.created_at && user.Patient_details.created_at !== "---" && (
                <div className="info-item">
                  <img src="/icons/date.png" alt="Date" />
                  <p>
                    {user.Patient_details.created_at.includes("T")
                      ? user.Patient_details.created_at.split("T")[0].split("-").reverse().join("-")
                      : user.Patient_details.created_at.split("-").reverse().join("-")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="your-div">
          <div
            className="flex flex-col w-full appointment-id-section"
            style={{ flex: 1, width: "100%" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "14px", color: "#3B362A", margin: 0 }}>Diabetes Summary</p>
              <IconButton size="small" onClick={handleOpenDiabetesEdit} sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }}>
                <Edit fontSize="small" />
              </IconButton>
            </div>

            <Dialog open={diabetesEditModalOpen} onClose={() => setDiabetesEditModalOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, color: "#3B362A" }}>
                Edit Diabetes Summary
              </DialogTitle>
              <DialogContent dividers>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", paddingTop: "8px" }}>
                  <FormControl fullWidth>
                    <InputLabel>Is Diabetic</InputLabel>
                    <Select
                      label="Is Diabetic"
                      value={editDiabetesForm.isDiabetic}
                      onChange={(e) => setEditDiabetesForm({ ...editDiabetesForm, isDiabetic: e.target.value })}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>

                  {editDiabetesForm.isDiabetic === "Yes" && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Diabetes Type</InputLabel>
                        <Select
                          label="Diabetes Type"
                          value={editDiabetesForm.diabetesType}
                          onChange={(e) => setEditDiabetesForm({ ...editDiabetesForm, diabetesType: e.target.value })}
                        >
                          <MenuItem value="Type 1">Type 1</MenuItem>
                          <MenuItem value="Type 2">Type 2</MenuItem>
                          <MenuItem value="Prediabetes">Prediabetes</MenuItem>
                          <MenuItem value="Gestational">Gestational</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        label="Fasting Glucose (mg/dL)"
                        type="number"
                        value={editDiabetesForm.fastingGlucose}
                        onChange={(e) => setEditDiabetesForm({ ...editDiabetesForm, fastingGlucose: e.target.value })}
                      />
                      
                      <TextField
                        fullWidth
                        label="HbA1c (%)"
                        type="number"
                        value={editDiabetesForm.hba1c}
                        onChange={(e) => setEditDiabetesForm({ ...editDiabetesForm, hba1c: e.target.value })}
                      />
                    </>
                  )}
                </div>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setDiabetesEditModalOpen(false)} sx={{ color: '#64748b' }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveDiabetesFollowup} 
                  variant="contained" 
                  disabled={savingDiabetesFollowup}
                  sx={{ backgroundColor: "var(--themePrimaryColor, #DA6E0B)", "&:hover": { backgroundColor: "#B85C09" } }}
                >
                  {savingDiabetesFollowup ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
                </Button>
              </DialogActions>
            </Dialog>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {/* Diabetes Type */}
              {diabetesFollowup?.isDiabetic && (
                <div style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                }}>
                  <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>Diabetes Type</span>
                  <p style={{ fontSize: "18px", fontWeight: 700, color: "#1e293b", fontFamily: "'Poppins', sans-serif", marginTop: "4px" }}>
                    {diabetesFollowup?.diabetesType || "---"}
                  </p>
                </div>
              )}

              {/* Is Diabetic */}
              <div style={{
                textAlign: "center",
                padding: "12px 8px",
                borderRadius: "10px",
                backgroundColor: "#fff",
                border: "1px solid #e2e8f0",
              }}>
                <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500, marginBottom: "4px", display: "block" }}>Is Diabetic</span>
                <Chip
                  label={diabetesFollowup ? (diabetesFollowup.isDiabetic ? "Yes" : "No") : "---"}
                  size="small"
                  sx={{
                    mt: 0.5,
                    fontWeight: 700,
                    fontFamily: "'Poppins', sans-serif",
                    backgroundColor: diabetesFollowup?.isDiabetic ? "#fef2f2" : "#f0fdf4",
                    color: diabetesFollowup?.isDiabetic ? "#dc2626" : "#16a34a",
                    border: diabetesFollowup?.isDiabetic ? "1px solid #fecaca" : "1px solid #bbf7d0",
                  }}
                />
              </div>

              {/* Fasting Glucose */}
              {diabetesFollowup?.isDiabetic && (
                <div style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", marginBottom: "4px" }}>
                    <Droplets size={14} className="text-[#3b82f6]" />
                    <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>Fasting Glucose</span>
                  </div>
                  <p style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", fontFamily: "'Poppins', sans-serif" }}>
                    {diabetesFollowup?.fastingGlucose ?? "---"}
                  </p>
                  <span style={{ fontSize: "10px", color: "#94a3b8", fontFamily: "'Poppins', sans-serif" }}>mg/dL</span>
                </div>
              )}

              {/* HbA1c */}
              {diabetesFollowup?.isDiabetic && (
                <div style={{
                  textAlign: "center",
                  padding: "12px 8px",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", marginBottom: "4px" }}>
                    <Flame size={14} className="text-[#8b5cf6]" />
                    <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>HbA1c</span>
                  </div>
                  <p style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b", fontFamily: "'Poppins', sans-serif" }}>
                    {diabetesFollowup?.hba1c ?? "---"}
                  </p>
                  <span style={{ fontSize: "10px", color: "#94a3b8", fontFamily: "'Poppins', sans-serif" }}>%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="Patient_Profile_list_container "
        style={{ alignItems: "flex-start", gap: "1rem" }}>
        <div className="Patient-more-info flex">
          <div className="flex pr-10">
            <div className="Patient-info ">

              {/* <div className="mt-2 Patient-wellnessCategories pb-6">

                {user?.Wellness_Category?.Categories && (
                  <p>Wellness Categories</p>
                )}

                <div className="flex flex-row mt-2 items-center flex-wrap gap-5">
                  {user?.Wellness_Category?.Categories?.map((e, i) => {
                    if (e.selected) {
                      return (
                        <div key={i} className="text-center">
                          <img
                            src={e.imageUrl}
                            alt={e?.name}
                            className="mx-auto mb-2 wellness-img"
                          />
                          <p>{e?.name}</p>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div> */}

              {user?.ClinicalStudy && (
                <div className="Patient-appointment-details p-4 mb-3 border rounded-md shadow-sm">
                  <h1 className="disease-heading text-lg font-semibold mb-2">Study Details</h1>
                  <div className="text-sm space-y-1">
                    <p>
                      <span className="font-medium">Group:</span>{" "}
                      <span className="text-[color:var(--themePrimaryColor,#374151)]">{user?.ClinicalStudy?.clinicalStudy}</span>
                    </p>
                    {user?.ClinicalStudy?.clinicalStudySub && (
                      <p>
                        <span className="font-medium">Sub-study Type:</span>{" "}
                        <span className="text-[color:var(--themePrimaryColor,#374151)]">{user.ClinicalStudy.clinicalStudySub}</span>
                      </p>
                    )}
                    {user?.ClinicalStudy?.StudyDuration && (
                      <p>
                        <span className="font-medium">Duration:</span>{" "}
                        <span className="text-[color:var(--themePrimaryColor,#374151)]">{user.ClinicalStudy.StudyDuration}</span>
                      </p>
                    )}
                    {user?.ClinicalStudy?.StudyStartDate && (
                      <p>
                        <span className="font-medium">Start Date:</span>{" "}
                        <span className="text-[color:var(--themePrimaryColor,#374151)]">{user.ClinicalStudy.StudyStartDate}</span>
                      </p>
                    )}
                    {user?.ClinicalStudy?.StudyendDate && (
                      <p>
                        <span className="font-medium">End Date:</span>{" "}
                        <span className="text-[color:var(--themePrimaryColor,#374151)]">{user.ClinicalStudy.StudyendDate}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {((diseaseListNew && diseaseListNew.length > 0) || (symptom && symptom.length > 0)) && (
                <div className="Patient-appointment-details p-4 mb-3 mt-6">
                  <h1 className="disease-heading">Diseases</h1>
                  <ul className="disease-list">
                    {diseaseListNew && diseaseListNew.length > 0 ? (
                      diseaseListNew.map((disease, index) => (
                        <li key={disease.id || index}>{disease.symptoms}</li>
                      ))
                    ) : (
                      <li>No complication</li>
                    )}
                  </ul>
                </div>
              )}

              {((symptom && symptom.length > 0) || (diseaseListNew && diseaseListNew.length > 0)) && (
                <div className="Patient-appointment-details p-4 mb-3">
                  <h1 className="disease-heading">Symptoms</h1>
                  <ul className="disease-list">
                    {symptom && symptom.length > 0 ? (
                      symptom.map((disease, index) => (
                        <li key={index}>{disease.symptoms}</li>
                      ))
                    ) : (
                      <li>No symptom</li>
                    )}
                  </ul>
                </div>
              )}
              <div className="flex flex-col gap-4 ">

                <div className="flex items-center justify-between bg-grey-300 p-4 rounded-lg w-full bg-stone-100">
                  <div
                    className="flex items-center gap-3"
                    onClick={() => {
                      setSelectedPatientdetails({ ...user?.Patient_details, ...(Array.isArray(user?.digital_params) ? [...user?.digital_params].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : user?.digital_params), Vikriti_details: user?.Vikriti_details });
                      if (user?.Appointment_details) setSelectedAppointemntdetails(user?.Appointment_details);
                      resetPrescriptionState();
                      navigate(`/Precription`);
                    }}
                  >
                    <div className="p-2 rounded-full bg-orange-50">
                      <FileText className="w-8 h-8 text-[#DA6E0B]" />
                    </div>
                    <p className=" font-medium"> Ayurvedic Prescription</p>
                  </div>
                  <IconButton
                    onClick={() => {
                      setSelectedPatientdetails({ ...user?.Patient_details, ...(Array.isArray(user?.digital_params) ? [...user?.digital_params].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : user?.digital_params), Vikriti_details: user?.Vikriti_details });
                      if (user?.Appointment_details) setSelectedAppointemntdetails(user?.Appointment_details);
                      resetPrescriptionState();
                      navigate(`/Precription`);
                    }}
                  >
                    <ChevronRight className="text-black" />
                  </IconButton>
                </div>
                <div className="flex items-center justify-between bg-grey-300 p-4 rounded-lg w-full bg-stone-100">
                  <div
                    className="flex items-center gap-3"
                    onClick={() => {
                      setSelectedPatientdetails({ ...user?.Patient_details, ...(Array.isArray(user?.digital_params) ? [...user?.digital_params].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : user?.digital_params), Vikriti_details: user?.Vikriti_details });
                      if (user?.Appointment_details) setSelectedAppointemntdetails(user?.Appointment_details);
                      resetPrescriptionState();
                      navigate(`/Precription`);
                    }}
                  >
                    <div className="p-2 rounded-full bg-orange-50">
                      <FileText className="w-8 h-8 text-[#DA6E0B]" />
                    </div>
                    <p className=" font-medium"> Modern Prescription</p>
                  </div>
                  <IconButton
                    onClick={() => {
                      setSelectedPatientdetails({ ...user?.Patient_details, ...(Array.isArray(user?.digital_params) ? [...user?.digital_params].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : user?.digital_params), Vikriti_details: user?.Vikriti_details });
                      if (user?.Appointment_details) setSelectedAppointemntdetails(user?.Appointment_details);
                      resetPrescriptionState();
                      navigate(`/Precription`);
                    }}
                  >
                    <ChevronRight className="text-black" />
                  </IconButton>
                </div>

                <div
                  className="flex items-center justify-between bg-stone-100 p-4 rounded-lg w-full cursor-pointer hover:bg-stone-200 transition-colors"
                  onClick={() => {
                    const uid = user?.Patient_details?.UID || id;
                    if (uid) navigate(`/QuestionAnswer/${uid}`);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-orange-50">
                      <ClipboardList className="w-8 h-8 text-[#DA6E0B]" />
                    </div>
                    <p className="text-black font-medium">Diabetes Assessment</p>
                  </div>
                  <IconButton>
                    <ChevronRight className="text-black" />
                  </IconButton>
                </div>

                <div
                  className="flex items-center justify-between bg-grey-300 p-4 rounded-lg w-full bg-stone-100 "
                  onClick={() => {
                    setSelectedPatientdetails({ ...user?.Patient_details, ...(Array.isArray(user?.digital_params) ? [...user?.digital_params].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : user?.digital_params), Vikriti_details: user?.Vikriti_details });
                    if (user?.Appointment_details) setSelectedAppointemntdetails(user?.Appointment_details);
                    navigate(
                      `/Reports/${user?.Patient_details?.UID}`
                    );
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-orange-50">
                      <FileBarChart2 className="w-8 h-8 text-[#DA6E0B]" />
                    </div>
                    <p className="text-black font-medium">Report</p>
                  </div>
                  <IconButton
                    component={Link}
                    to={`/Reports/${user?.Patient_details?.UID}`}
                  >
                    <ChevronRight className="text-black" />
                  </IconButton>
                </div>


                {hasHeartSessionData && (
                  <div
                    className="flex items-center justify-between bg-grey-300 p-4 rounded-lg w-full bg-stone-100 cursor-pointer"
                    onClick={() => {
                      navigate(`/HeartAnalysis/${user?.Patient_details?.UID}`);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-orange-50">
                        <HeartPulse className="w-8 h-8 text-[#DA6E0B]" />
                      </div>
                      <p className="text-black font-medium">Heart Analysis</p>
                    </div>
                    <IconButton>
                      <ChevronRight className="text-black" />
                    </IconButton>
                  </div>
                )}

                {hasFacialLandmarkData && (
                  <div
                    className="flex items-center justify-between bg-grey-300 p-4 rounded-lg w-full bg-stone-100 cursor-pointer"
                    onClick={() => {
                      navigate(`/FacialAnalysis/${user?.Patient_details?.UID}`);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-orange-50">
                        <ScanFace className="w-8 h-8 text-[#DA6E0B]" />
                      </div>
                      <p className="text-black font-medium">Facial Analysis</p>
                    </div>
                    <IconButton>
                      <ChevronRight className="text-black" />
                    </IconButton>
                  </div>
                )}



              </div>




            </div>
          </div>

          <div className="flex-1" style={{ boxSizing: "border-box" }}>

            <div className="flex gap-4 items-stretch">

              <div className="PrakritiChart flex-1 flex flex-col">

                <div className="Prakriti-result-item flex-1">
                  <p>Prakriti Result</p>

                  {user?.Prakriti_details && (
                    <div style={{ height: 180, width: '100%', marginBottom: '10px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: "Vata",
                                value: Number(user?.Prakriti_details?.vata ?? 0).toFixed(2),
                              },
                              {
                                name: "Pitta",
                                value: Number(user?.Prakriti_details?.pitta ?? 0).toFixed(2),
                              },
                              {
                                name: "Kapha",
                                value: Number(user?.Prakriti_details?.kapha ?? 0).toFixed(2),
                              },
                            ].map(item => ({
                              ...item,
                              value: Number(item.value),
                            }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            <Cell fill="var(--themePrimaryColor, #312110)" />
                            <Cell fill="#FF5C00" />
                            <Cell fill="#6CBA6C" />
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="Prakriti-r-c-item mt-4">
                    <p>Prakriti Assesment</p>
                    <div className="mt-2 ass-lables">
                      <div className="flex flex-row items-center justify-evenly">
                        <p>{user?.Prakriti_details?.result} {user?.Prakriti_details?.result && 'dominant'}</p>
                        <div>
                          <IconButton onClick={handleOpen4}>
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            onClick={() => {
                              setPrakritiCollection(
                                user?.Prakriti_details?.PrakritiCollection
                              );

                              handleOpen();
                            }}
                          >
                            <UpgradeIcon />
                          </IconButton>
                        </div>
                      </div>
                      {
                        user?.Prakriti_details &&
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-around",
                            alignItems: "center",
                            width: "100%",
                          }}
                          className="mt-6 ass-lables"
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "var(--themePrimaryColor, #312110)",
                                marginRight: "8px",
                              }}
                            ></div>
                            <p className="pr-2">V :</p>
                            <p variant="body1">
                              {Math.round(user?.Prakriti_details?.vata)}%
                            </p>
                          </div>

                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#FF5C00",
                                marginRight: "8px",
                              }}
                            ></div>
                            <p className="pr-2">P :</p>
                            <p variant="body1">
                              {Math.round(user?.Prakriti_details?.pitta)}%
                            </p>
                          </div>

                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#6CBA6C",
                                marginRight: "8px",
                              }}
                            ></div>
                            <p className="pr-2">K :</p>
                            <p variant="body1">
                              {Math.round(user?.Prakriti_details?.kapha)}%
                            </p>
                          </div>
                        </div>
                      }

                    </div>
                  </div>
                </div>



              </div>

              <div className="VikritiChart flex-1 flex flex-col">
                <div className="Vikriti-result-item flex-1">
                  <p>Vikriti</p>
                  {!diagnosisData &&
                    <div className="mt-5">

                      <DoshaBarChart
                        vikritiresultData={vikritiscore}
                        style={{ border: "1px solid red" }}
                      />

                    </div>
                  }

                  <div className="Prakriti-r-c-item">
                    <p>Dosha Analysis</p>
                    <div className="mt-2 ass-lables">

                      <div className="flex items-center justify-between w-full">
                        {(diagnosisData?.increased_dosha?.dosha || user?.Vikriti_details?.Result) && (
                          <p className="text-sm">
                            {(diagnosisData
                              ? diagnosisData?.increased_dosha?.dosha?.toUpperCase()
                              : user?.Vikriti_details?.Result?.split('')[0]?.toUpperCase())}{" "}
                            dominant
                          </p>
                        )}

                        <IconButton onClick={handleOpen5}>
                          <UpgradeIcon />
                        </IconButton>
                      </div>

                      <div className="mt-2 text-sm">
                        <span className="font-semibold text-[var(--themePrimaryColor, #3B362A)]">Reason : </span>
                        <span className="text-gray-600">
                          {user?.Vikriti_details?.reason && user?.Vikriti_details?.reason !== "None" ? (
                            user?.Vikriti_details?.reason
                          ) : vikritiReason ? (
                            vikritiReason
                          ) : diagnosisData?.increased_dosha?.reason && diagnosisData?.increased_dosha?.reason !== "None" ? (
                            diagnosisData?.increased_dosha?.reason
                          ) : isVikritiReasonLoading ? (
                            <span className="text-[color:var(--themePrimaryColor,#DA6E0B)] font-medium">Generating...</span>
                          ) : (
                            "None"
                          )}
                        </span>
                      </div>



                      <div>
                        {/* <button onClick={handleOpenAi}>AI Suggestion</button> */}
                        {openAi && (
                          <div className="flex flex-col space-y-4">
                            {loading ? (
                              <div className="mt-5 flex-1 flex items-center justify-center">
                                <CircularProgress size="30px" sx={{ color: "var(--themePrimaryColor, #DA6E0B)" }} /></div>
                            ) : (
                              <>
                                <div className="flex items-start space-x-2 mt-4">
                                  <img
                                    src="/images/increaseDosha.png"
                                    className="w-14 h-14"
                                    alt="Increased Dosha"
                                  />
                                  <div>
                                    <p className="font-semibold text-sm">
                                      Increased Dosha:{" "}
                                      {diagnosisData?.increased_dosha?.dosha}
                                    </p>
                                    <p className="text-sm text-[color:var(--themePrimaryColor,#374151)]">
                                      {diagnosisData?.increased_dosha?.reason}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start space-x-2">
                                  <img
                                    src="/images/decreaseDosha.png"
                                    className="w-14 h-14"
                                    alt="Decreased Dosha"
                                  />
                                  <div>
                                    <p className="font-semibold text-sm">
                                      Decreased Dosha:{" "}
                                      {diagnosisData?.decreased_dosha?.dosha}
                                    </p>
                                    <p className="text-sm text-[color:var(--themePrimaryColor,#374151)]">
                                      {diagnosisData?.decreased_dosha?.reason}
                                    </p>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* <div className="flex flex-row mt-10">
                      <button
                        style={{
                          width: "100%",
                          textTransform: "none",
                          borderRadius: "0",
                          border: "1px solid var(--themePrimaryColor, #DA6E0B) ",
                          color: "var(--themePrimaryColor, #DA6E0B)",
                          boxSizing: "border-box",
                          borderRadius: "5px",
                          fontSize: "14px",
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 500,
                          padding: "2px",
                        }}
                        onClick={handleOpen3}
                        className="mr-2"
                      >
                        Enter Manually
                      </button>

                      <button
                        style={{
                          width: "100%",
                          textTransform: "none",
                          borderRadius: "0",
                          background: "var(--themePrimaryColor, #DA6E0B) ",
                          color: "var(--themeSurfaceColor, #FDFDFD)",
                          boxSizing: "border-box",
                          borderRadius: "5px",
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 500,
                          padding: "10px",
                          fontSize: "14px",
                        }}
                        onClick={() => {
                          diagnosisData != null ? handleSaveAISuggestion(diagnosisData) :
                            handleOpenAi()
                        }}
                      >
                        {diagnosisData != null ? "Save" : "AI Suggestion"}
                      </button>

                    </div> */}
                  </div>
                </div>
              </div>

            </div>

            {/* Dietary Recommendations Section */}
            {/* <div className="mt-10 mb-10">
              <CollapsibleSection title="Dietary Recommendations">
                {isDietLoading ? (
                  <div className="flex justify-center p-4">
                    <CircularProgress sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />
                  </div>
                ) : (
                  <div className="DietRecommendation-add flex flex-row justify-between mt-2 gap-8">
                    <div className="dietrc">
                      <div className="DietHeader">
                        <p>Food to be included</p>
                      </div>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={(dietRecommendations?.data?.FoodtobeTaken || dietRecommendations?.Suitable || []).map(item => item.englishName || item)}
                        readOnly
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option}
                              {...getTagProps({ index })}
                              sx={{
                                backgroundColor: 'var(--themeSecondaryColor, #efefe7)',
                                color: 'var(--themePrimaryColor, #3B362A)',
                                borderRadius: '5px',
                                fontWeight: 500,
                                fontFamily: "'Poppins', sans-serif"
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder=""
                            fullWidth
                            sx={{
                              backgroundColor: 'var(--themeSurfaceColor, #FDFDFD)',
                              input: {
                                color: 'var(--themePrimaryColor, #3B362A)',
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 500
                              },
                              '.MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#ccc' },
                                '&:hover fieldset': { borderColor: '#aaa' },
                                '&.Mui-focused fieldset': { bordercolor: 'var(--themePrimaryColor, #312110)' }
                              }
                            }}
                          />
                        )}
                      />
                    </div>

                    <div className="dietrc">
                      <div className="DietHeader">
                        <p>Food to be Minimal</p>
                      </div>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={(dietRecommendations?.data?.Foodtobeavoided || dietRecommendations?.Not_Suitable || []).map(item => item.englishName || item)}
                        readOnly
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option}
                              {...getTagProps({ index })}
                              sx={{
                                backgroundColor: 'var(--themeSecondaryColor, #efefe7)',
                                color: 'var(--themePrimaryColor, #3B362A)',
                                borderRadius: '5px',
                                fontWeight: 500,
                                fontFamily: "'Poppins', sans-serif"
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder=""
                            fullWidth
                            sx={{
                              backgroundColor: 'var(--themeSurfaceColor, #FDFDFD)',
                              input: {
                                color: 'var(--themePrimaryColor, #3B362A)',
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 500
                              },
                              '.MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#ccc' },
                                '&:hover fieldset': { borderColor: '#aaa' },
                                '&.Mui-focused fieldset': { bordercolor: 'var(--themePrimaryColor, #312110)' }
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}
              </CollapsibleSection>
            </div> */}

            {/* Lifestyle Practices Section */}
            {/* <div className="mt-10 mb-10">
              <CollapsibleSection title="Lifestyle Practices">
                {isLifestyleLoading ? (
                  <div className="flex justify-center p-4">
                    <CircularProgress sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />
                  </div>
                ) : (
                  <div>
                    <div className="DietRecommendation-add" style={{ display: "flex", flexDirection: "row", gap: "32px", marginTop: "16px" }}>
                      <div className="practices-card flex-1">
                        <div className="flex justify-between mb-2">
                          <p className="font-semibold text-[var(--themePrimaryColor, #3B362A)] font-['Poppins']">Yoga</p>
                        </div>
                        {renderTable("yoga", lifestylePractices?.yoga || [], "Reps")}
                      </div>

                      <div className="practices-card flex-1">
                        <p className="flex justify-between mb-2 font-semibold text-[var(--themePrimaryColor, #3B362A)] font-['Poppins']">
                          Pranayama
                        </p>
                        {renderTable("pranayama", lifestylePractices?.pranayama || [], "Reps")}
                      </div>

                      <div className="practices-card flex-1">
                        <p className="flex justify-between mb-2 font-semibold text-[var(--themePrimaryColor, #3B362A)] font-['Poppins']">
                          Meditation
                        </p>
                        {renderTable("meditation", lifestylePractices?.meditation || [], "Duration")}
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleSection>
            </div> */}



            {/* Shared Analytics Dialog */}
            <Dialog open={analyticsModalOpen} onClose={() => setAnalyticsModalOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle sx={{ m: 0, p: 2, background: '#fff8f0', color: 'var(--themePrimaryColor, #DA6E0B)', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                {analyticsType === 'dp' ? 'Digital Parameters Analytics' : 'Body Composition Analytics'}
                <IconButton
                  aria-label="close"
                  onClick={() => setAnalyticsModalOpen(false)}
                  sx={{ position: 'absolute', right: 8, top: 8, color: 'var(--themePrimaryColor, #DA6E0B)' }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ backgroundColor: '#ffffff', p: 3 }}>
                {analyticsType === 'dp' ? (() => {
                  const allDp = (Array.isArray(user?.digital_params) ? user?.digital_params : (user?.digital_params ? [user?.digital_params] : [])).filter(Boolean)
                    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

                  const uniqueDp = [];
                  const seenDpDays = new Set();
                  for (const r of allDp) {
                    if (!r.createdAt) continue;
                    const dayStr = new Date(r.createdAt).toLocaleDateString('en-IN');
                    if (!seenDpDays.has(dayStr)) {
                      uniqueDp.push(r);
                      seenDpDays.add(dayStr);
                    }
                    if (uniqueDp.length >= 5) break;
                  }

                  const dpChartData = [...uniqueDp]
                    .reverse()
                    .map(rec => ({
                      date: rec?.createdAt ? new Date(rec.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '---',
                      fullDate: rec?.createdAt ? new Date(rec.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '---',
                      systolic: (Number(rec?.systolic) || null),
                      diastolic: (Number(rec?.diastolic) || null),
                      pulse: (Number(rec?.pulse) || null),
                      spo2: (Number(rec?.spo2) || null),
                      rbs: (Number(rec?.rbs) || null),
                    }));

                  const renderChartSet = (title, dataKey, color, icon, unit, domain) => (
                    <div key={dataKey} className="p-4 border border-gray-100 rounded-xl" style={{ background: `linear-gradient(180deg, ${color}08 0%, #ffffff 100%)` }}>
                      <h3 className="text-sm font-semibold text-[color:var(--themePrimaryColor,#374151)] mb-4 font-['Poppins'] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Activity size={16} style={{ color }} /> {title}
                        </div>
                        <span style={{ color, fontSize: 11 }}>{unit}</span>
                      </h3>
                      <div className="h-44 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={dpChartData.filter(d => d[dataKey] !== null)} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.15} />
                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} domain={domain || ['auto', 'auto']} />
                            <RechartsTooltip
                              contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '11px' }}
                              labelFormatter={(v, n, props) => (props && props.length > 0 && props[0]?.payload?.fullDate) ? props[0].payload.fullDate : v}
                            />
                            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} fillOpacity={1} fill={`url(#grad-${dataKey})`} connectNulls={true} dot={{ r: 3.5, fill: color, stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">
                      {renderChartSet('Systolic BP', 'systolic', '#ef4444', Activity, 'mmHg', [80, 200])}
                      {renderChartSet('Diastolic BP', 'diastolic', '#3b82f6', Activity, 'mmHg', [50, 110])}
                      {renderChartSet('Pulse Rate', 'pulse', '#f59e0b', Activity, 'bpm', [50, 130])}
                      {renderChartSet('Oxygen (SpO2)', 'spo2', '#10b981', Activity, '%', [90, 100])}
                      {uniqueDp.some(r => r?.rbs !== undefined && r?.rbs !== null && r?.rbs !== '') && renderChartSet('Blood Sugar', 'rbs', '#8b5cf6', Activity, 'mg/dL', [60, 250])}
                    </div>
                  );
                })() : (() => {
                  const allBc = (Array.isArray(user?.body_composition) ? user?.body_composition : (user?.body_composition ? [user?.body_composition] : [])).filter(Boolean)
                    .sort((a, b) => new Date(b.measuredAt || 0) - new Date(a.measuredAt || 0));

                  const uniqueBc = [];
                  const seenBcDays = new Set();
                  for (const r of allBc) {
                    if (!r.measuredAt) continue;
                    const dayStr = new Date(r.measuredAt).toLocaleDateString('en-IN');
                    if (!seenBcDays.has(dayStr)) {
                      uniqueBc.push(r);
                      seenBcDays.add(dayStr);
                    }
                    if (uniqueBc.length >= 5) break;
                  }

                  const bcChartData = [...uniqueBc]
                    .reverse()
                    .map(rec => ({
                      date: rec?.measuredAt ? new Date(rec.measuredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '---',
                      fullDate: rec?.measuredAt ? new Date(rec.measuredAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '---',
                      bodyFat: Number(rec?.bodyFat?.value !== undefined ? rec.bodyFat.value : rec?.bodyFat) || null,
                      muscleRate: Number(rec?.muscleRate?.value !== undefined ? rec.muscleRate.value : rec?.muscleRate) || null,
                      bmr: Number(rec?.bmr) || null,
                      bmi: Number(rec?.bmi) || null,
                      subCutFat: Number(rec?.bhBodyFatSubCutRate?.value !== undefined ? rec.bhBodyFatSubCutRate.value : rec?.bhBodyFatSubCutRate) || null,
                      vfal: Number(rec?.vfal) || null,
                    }));

                  const renderChartSet = (title, dataKey, color, icon, unit, domain) => (
                    <div key={dataKey} className="p-4 border border-gray-100 rounded-xl" style={{ background: `linear-gradient(180deg, ${color}08 0%, #ffffff 100%)` }}>
                      <h3 className="text-sm font-semibold text-[color:var(--themePrimaryColor,#374151)] mb-4 font-['Poppins'] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Scale size={16} style={{ color }} /> {title}
                        </div>
                        <span style={{ color, fontSize: 11 }}>{unit}</span>
                      </h3>
                      <div className="h-44 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={bcChartData.filter(d => d[dataKey] !== null)} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.15} />
                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} domain={domain || ['auto', 'auto']} />
                            <RechartsTooltip
                              contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '11px' }}
                              labelFormatter={(v, n, props) => (props && props.length > 0 && props[0]?.payload?.fullDate) ? props[0].payload.fullDate : v}
                            />
                            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} fillOpacity={1} fill={`url(#grad-${dataKey})`} connectNulls={true} dot={{ r: 3.5, fill: color, stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">
                      {renderChartSet('Body Fat', 'bodyFat', '#ef4444', Scale, '%', [5, 45])}
                      {/* {renderChartSet('Skeletal Muscle', 'muscleRate', '#3b82f6', Scale, '%', [15, 60])} */}
                      {renderChartSet('BMI', 'bmi', '#10b981', Scale, '', [15, 40])}
                      {renderChartSet('BMR', 'bmr', '#8b5cf6', Scale, 'kcal', ['auto', 'auto'])}
                      {renderChartSet('Subcutaneous Fat', 'subCutFat', '#ec4899', Scale, '%', [5, 40])}
                      {renderChartSet('Visceral Fat', 'vfal', '#6366f1', Scale, 'Level', [0, 25])}
                    </div>
                  );
                })()}
              </DialogContent>
            </Dialog>




          </div >


        </div>

      </div>


      {user?.digital_params && (() => {
        const allDp = (Array.isArray(user?.digital_params) ? user?.digital_params : (user?.digital_params ? [user?.digital_params] : []))
          .filter(Boolean)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

        const dpArray = [];
        const dpSeenDays = new Set();
        for (const r of allDp) {
          const dayStr = new Date(r.createdAt || 0).toLocaleDateString('en-IN');
          if (!dpSeenDays.has(dayStr)) {
            dpArray.push(r);
            dpSeenDays.add(dayStr);
          }
          if (dpArray.length >= 5) break;
        }

        const trendArrow = (curr, prev) => {
          if (prev === undefined || prev === null) return null;
          const c = Number(curr), p = Number(prev);
          if (isNaN(c) || isNaN(p)) return null;
          if (c > p) return <span style={{ color: '#ef4444', fontWeight: 700 }}>↑</span>;
          if (c < p) return <span style={{ color: '#22c55e', fontWeight: 700 }}>↓</span>;
          return <span style={{ color: '#9ca3af', fontWeight: 700 }}>→</span>;
        };

        const statusPill = (st) => {
          if (!st) return null;
          const cls = st === 'high' ? 'bg-red-100 text-red-700' : st === 'low' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
          return <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ml-1 ${cls}`}>{st}</span>;
        };

        const dpRows = [
          {
            label: 'Blood Pressure', unit: 'mmHg', range: '90/60–120/80',
            getValue: r => (r?.systolic && r?.diastolic) ? `${r.systolic}/${r.diastolic}` : null,
            getStatus: r => {
              if (!r?.systolic || !r?.diastolic) return null;
              const s = Number(r?.systolic);
              const d = Number(r?.diastolic);
              if (s > 120 || d > 80) return 'high';
              if (s < 90 || d < 60) return 'low';
              return 'normal';
            },
          },
          {
            label: 'Pulse Rate', unit: 'bpm', range: '60–100',
            getValue: r => r?.pulse,
            getStatus: r => r?.pulse ? (Number(r?.pulse) > 100 ? 'high' : Number(r?.pulse) < 60 ? 'low' : 'normal') : null,
          },
          {
            label: 'SpO2', unit: '%', range: '90–100',
            getValue: r => r?.spo2,
            getStatus: r => r?.spo2 ? (Number(r?.spo2) > 100 ? 'high' : Number(r?.spo2) < 90 ? 'low' : 'normal') : null,
          },
        ];

        if (dpArray.some(r => r?.rbs !== undefined && r?.rbs !== null && r?.rbs !== '')) {
          dpRows.push({
            label: 'Blood Sugar (RBS)', unit: 'mg/dL', range: '70–200',
            getValue: r => r?.rbs,
            getStatus: r => r?.rbs ? (Number(r?.rbs) > 200 ? 'high' : Number(r?.rbs) < 70 ? 'low' : 'normal') : null,
          });
        }

        const gripStrengthRanges = [
          { minAge: 0, maxAge: 24, male: [36.8, 56.6], female: [21.5, 35.3] },
          { minAge: 25, maxAge: 29, male: [37.7, 57.5], female: [25.6, 41.0] },
          { minAge: 30, maxAge: 34, male: [36.0, 55.4], female: [21.0, 34.0] },
          { minAge: 35, maxAge: 39, male: [35.8, 55.0], female: [20.3, 33.1] },
          { minAge: 40, maxAge: 44, male: [35.5, 54.3], female: [18.9, 32.3] },
          { minAge: 45, maxAge: 49, male: [34.7, 53.7], female: [18.6, 31.4] },
          { minAge: 50, maxAge: 54, male: [32.9, 51.3], female: [17.4, 30.4] },
          { minAge: 55, maxAge: 59, male: [30.7, 48.9], female: [15.9, 28.7] },
          { minAge: 60, maxAge: 64, male: [30.2, 48.0], female: [15.2, 27.3] },
          { minAge: 65, maxAge: 69, male: [28.2, 44.3], female: [14.7, 24.5] },
          { minAge: 70, maxAge: 74, male: [26.5, 42.3], female: [14.0, 23.0] },
          { minAge: 75, maxAge: Infinity, male: [23.1, 38.1], female: [12.2, 20.9] },
        ];

        const getHandgripSeverity = (handgripValue, profile) => {
          const handgrip = parseFloat(handgripValue);
          
          if (isNaN(handgrip) || handgrip === null || handgrip === undefined) {
            return { status: "Unknown", color: "gray", refRange: "---" };
          }

          const gender = (profile?.gender || "male").toLowerCase().trim();
          let age = Number(profile?.age);
          if (!age && profile?.dob) {
            age = (Date.now() - new Date(profile.dob).getTime()) / (365.25 * 24 * 3600 * 1000);
          }
          if (!age || isNaN(age) || age < 0) age = 35; // Default age if missing, invalid, or negative (future DOB)
          age = Math.floor(age); // Guarantee integer to prevent falling between range gaps

          const range = gripStrengthRanges.find(r => age >= r.minAge && age <= r.maxAge);
          if (!range) {
            return { status: "Unknown", color: "gray", refRange: "---" };
          }

          const isFemale = gender === "female" || gender === "f";
          const [min, max] = isFemale ? range.female : range.male;
          const refRange = `${min} - ${max}`; // Omit ' kg' so it matches the UI styling which appends 'kg'

          if (handgrip < min) {
            return { status: "Weak/Low", color: "#D97706", refRange: refRange };
          } else {
            return { status: "Normal/Strong", color: "#059669", refRange: refRange };
          }
        };

        const hasGrip = r => (r?.gripStrengthLeft !== undefined && r?.gripStrengthLeft !== null && r?.gripStrengthLeft !== '') || 
                             (r?.gripStrengthRight !== undefined && r?.gripStrengthRight !== null && r?.gripStrengthRight !== '');

        if (dpArray.some(hasGrip)) {
          const profile = user?.Patient_details;
          const dummySeverity = getHandgripSeverity(25, profile); // Dummy value to safely extract refRange

          dpRows.push({
            label: 'Grip Strength', unit: 'kg', range: dummySeverity.refRange || '---',
            getValue: r => {
              const hasL = r?.gripStrengthLeft !== undefined && r?.gripStrengthLeft !== null && r?.gripStrengthLeft !== '';
              const hasR = r?.gripStrengthRight !== undefined && r?.gripStrengthRight !== null && r?.gripStrengthRight !== '';
              const left = parseFloat(r?.gripStrengthLeft);
              const right = parseFloat(r?.gripStrengthRight);
              
              if (hasL && hasR && !isNaN(left) && !isNaN(right)) return Number(((left + right) / 2).toFixed(1));
              if (hasL && !isNaN(left)) return Number(left.toFixed(1));
              if (hasR && !isNaN(right)) return Number(right.toFixed(1));
              return null;
            },
            getStatus: r => {
              const hasL = r?.gripStrengthLeft !== undefined && r?.gripStrengthLeft !== null && r?.gripStrengthLeft !== '';
              const hasR = r?.gripStrengthRight !== undefined && r?.gripStrengthRight !== null && r?.gripStrengthRight !== '';
              if (!hasL && !hasR) return null;
              
              const left = parseFloat(r?.gripStrengthLeft);
              const right = parseFloat(r?.gripStrengthRight);
              let avg = null;
              
              if (hasL && hasR && !isNaN(left) && !isNaN(right)) avg = ((left + right) / 2);
              else if (hasL && !isNaN(left)) avg = left;
              else if (hasR && !isNaN(right)) avg = right;

              if (avg === null || isNaN(avg)) return null;

              const severity = getHandgripSeverity(avg, profile);
              if (severity.status === 'Unknown') return null;
              if (severity.status === 'Weak/Low') return 'low';
              return 'normal';
            },
          });
        }

        // Prepare Chart Data
        const dpChartData = [...dpArray].reverse().map(rec => ({
          date: rec?.createdAt ? new Date(rec.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '---',
          systolic: Number(rec?.systolic) || 0,
          diastolic: Number(rec?.diastolic) || 0,
          pulse: Number(rec?.pulse) || 0,
          spo2: Number(rec?.spo2) || 0,
          rbs: Number(rec?.rbs) || 0,
        }));

        if (dpArray.length === 0) return null;

        return (
          <Accordion
            expanded={dpExpanded}
            onChange={() => setDpExpanded(!dpExpanded)}
            sx={{
              width: "100%",
              mt: 3,
              borderRadius: "8px !important",
              boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
              border: "1px solid #e5e7eb",
              backgroundColor: '#f8f8f7',
              "&:before": { display: "none" }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />}
              sx={{ p: "8px 24px", minHeight: "64px", "& .MuiAccordionSummary-content": { m: 0 } }}
            >
              <div className="flex justify-between items-center w-full pr-4">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  <Activity className="w-5 h-5 text-[color:var(--themePrimaryColor,#DA6E0B)]" />
                  Digital Parameters
                  <span className="ml-2 text-xs font-normal bg-orange-100 text-orange-700 px-2.5 py-0.5 rounded-full">
                    {dpArray.length} {dpArray.length === 1 ? 'record' : 'records'}
                  </span>
                </h2>
              </div>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3, pt: 0, backgroundColor: '#f8f8f7' }}>
              <div className="flex justify-end gap-3 mb-4">
                {dpArray.length > 1 && <Button
                  variant="outlined"
                  onClick={() => openAnalytics('dp')}
                  startIcon={<BarChart2 size={16} />}
                  sx={{
                    color: "var(--themePrimaryColor, #DA6E0B)",
                    borderColor: "var(--themePrimaryColor, #DA6E0B)",
                    "&:hover": { borderColor: "#B85C09", backgroundColor: "#fff8f0" },
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "6px",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Analytics
                </Button>}
                <Button
                  variant="contained"
                  onClick={SendReportsToWBHKiosk}
                  disabled={loadingReport}
                  sx={{
                    bgcolor: "var(--themePrimaryColor, #DA6E0B)",
                    "&:hover": { bgcolor: "#B85C09" },
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "6px",
                    fontFamily: "'Poppins', sans-serif",
                    boxShadow: "0 4px 6px -1px rgba(218, 110, 11, 0.2)",
                  }}
                >
                  {loadingReport ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Generate Report"}
                </Button>
                {user?.kioskreport?.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={() => setViewReportDialogOpen(true)}
                    sx={{
                      bgcolor: "#4CAF50",
                      "&:hover": { bgcolor: "#45a049" },
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "6px",
                      fontFamily: "'Poppins', sans-serif",
                      boxShadow: "0 4px 6px -1px rgba(76, 175, 80, 0.2)",
                    }}
                  >
                    View Report
                  </Button>
                )}
              </div>

              <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '10px 14px', background: '#fff8f0', color: 'var(--themePrimaryColor, #DA6E0B)', fontWeight: 600, borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)', whiteSpace: 'nowrap', minWidth: 150 }}>Parameter</th>
                      {dpArray.length > 0 && (
                        <th style={{
                          textAlign: 'center', padding: '12px 16px', whiteSpace: 'nowrap', borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)',
                          background: '#fff3e0', color: 'var(--themePrimaryColor, #DA6E0B)', fontWeight: 600, minWidth: '130px'
                        }}>
                          <span style={{ display: 'block', fontSize: 10, color: '#f97316', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 800 }}>Latest</span>
                          {dpArray[0].createdAt ? new Date(dpArray[0].createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: '#94a3b8', marginTop: 2 }}>
                            {dpArray[0].createdAt ? new Date(dpArray[0].createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </th>
                      )}
                      {dpArray.length > 1 && (
                        <th style={{ textAlign: 'center', padding: '10px 14px', background: '#fff8f0', color: 'var(--themePrimaryColor, #DA6E0B)', fontWeight: 600, borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)', whiteSpace: 'nowrap', width: 60 }}>Trend</th>
                      )}
                      {dpArray.slice(1).map((rec, i) => (
                        <th key={i} style={{
                          textAlign: 'center', padding: '12px 16px', whiteSpace: 'nowrap', borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)',
                          background: 'transparent', color: '#64748b', fontWeight: 600, minWidth: '130px'
                        }}>
                          {rec.createdAt ? new Date(rec.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                          <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: '#94a3b8', marginTop: 2 }}>
                            {rec.createdAt ? new Date(rec.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dpRows.map((row, ri) => (
                      <tr key={ri} style={{ background: ri % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                        <td style={{ padding: '12px 16px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9' }}>
                          <div style={{ fontWeight: 600, fontSize: '14px' }}>{row.label}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{row.range} {row.unit}</div>
                        </td>
                        {dpArray.length > 0 && (() => {
                          const val = row.getValue(dpArray[0]);
                          const st = row.getStatus(dpArray[0]);
                          return (
                            <td style={{
                              padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #f1f5f9',
                              background: 'rgba(255,243,224,0.3)', fontWeight: 700, fontSize: '14px', color: '#1e293b'
                            }}>
                              <div className="flex flex-col items-center justify-center gap-1">
                                {val !== null && val !== 0 && val !== '0' && val !== '' ? (
                                  <div className="flex flex-col items-center">
                                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                                      {(() => {
                                        if (typeof val === 'string' && val.includes('/')) return val;
                                        const n = Number(val);
                                        return isNaN(n) ? val : n.toFixed(2);
                                      })()}
                                    </span>
                                    {statusPill(st)}
                                  </div>
                                ) : '---'}
                              </div>
                            </td>
                          );
                        })()}
                        {dpArray.length > 1 && (
                          <td style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9' }}>
                            {(() => {
                              const v1 = row.getValue(dpArray[0]);
                              const v2 = dpArray[1] ? row.getValue(dpArray[1]) : null;
                              const trend = (v1 && v2) ? getTrend(v2, v1) : null;
                              return trend && trend !== '→' ? (
                                <span style={{ color: trend === '↑' ? '#ef4444' : '#22c55e', fontWeight: 900, fontSize: 16 }}>{trend}</span>
                              ) : <span style={{ color: '#94a3b8' }}>---</span>;
                            })()}
                          </td>
                        )}
                        {dpArray.slice(1).map((rec, ci) => {
                          const val = row.getValue(rec);
                          return (
                            <td key={ci} style={{
                              padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #f1f5f9',
                              background: 'transparent', fontWeight: 500, fontSize: '14px', color: '#64748b'
                            }}>
                              <div className="flex flex-col items-center justify-center gap-1">
                                {val !== null && val !== 0 && val !== '0' && val !== '' ? (
                                  <div className="flex flex-col items-center">
                                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                                      {(() => {
                                        if (typeof val === 'string' && val.includes('/')) return val;
                                        const n = Number(val);
                                        return isNaN(n) ? val : n.toFixed(2);
                                      })()}
                                    </span>
                                  </div>
                                ) : '---'}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AccordionDetails>
          </Accordion>
        );
      })()}




      {user?.body_composition && (() => {
        const allBc = (Array.isArray(user?.body_composition) ? user?.body_composition : (user?.body_composition ? [user?.body_composition] : []))
          .filter(Boolean)
          .sort((a, b) => new Date(b.measuredAt || 0) - new Date(a.measuredAt || 0));

        // console.log("Body Composition Data (Rendered):", allBc);

        const bcArray = [];
        const bcSeenDays = new Set();
        for (const r of allBc) {
          const dayStr = new Date(r.measuredAt || 0).toLocaleDateString('en-IN');
          if (!bcSeenDays.has(dayStr)) {
            bcArray.push(r);
            bcSeenDays.add(dayStr);
          }
          if (bcArray.length >= 5) break;
        }

        const trendArrow = (curr, prev) => {
          if (prev === undefined || prev === null) return null;
          const c = Number(curr), p = Number(prev);
          if (isNaN(c) || isNaN(p)) return null;
          if (c > p) return <span style={{ color: '#ef4444', fontWeight: 700 }}>↑</span>;
          if (c < p) return <span style={{ color: '#22c55e', fontWeight: 700 }}>↓</span>;
          return <span style={{ color: '#9ca3af', fontWeight: 700 }}>→</span>;
        };

        const statusPill = (st) => {
          if (!st) return null;
          const cls = st === 'high' ? 'bg-red-100 text-red-700' : st === 'low' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
          return <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ml-1 ${cls}`}>{st}</span>;
        };

        const getBodyFatReference = (gender, ageStr) => {
          const age = parseInt(ageStr, 10) || 30; // default to 30 if age is unknown
          const isMale = gender === 'Male' || gender === 'male' || gender === 'M';

          if (isMale) {
            if (age < 30) return { min: 14, max: 24, obese: 25 };
            if (age < 40) return { min: 15, max: 25, obese: 26 };
            if (age < 50) return { min: 16, max: 26, obese: 27 };
            if (age < 60) return { min: 17, max: 27, obese: 28 };
            return { min: 18, max: 28, obese: 29 };
          } else {
            if (age < 30) return { min: 21, max: 31, obese: 32 };
            if (age < 40) return { min: 22, max: 32, obese: 33 };
            if (age < 50) return { min: 23, max: 33, obese: 34 };
            if (age < 60) return { min: 24, max: 34, obese: 35 };
            return { min: 25, max: 35, obese: 36 };
          }
        };

        const bfRef = getBodyFatReference(user?.Patient_details?.gender, user?.Patient_details?.dob);

        const bcRows = [
          { label: 'Weight', unit: r => r?.weight?.unit || 'kg', range: '---', getValue: r => r?.weight?.value !== undefined ? r.weight.value : (typeof r?.weight === 'object' ? null : r?.weight), getStatus: () => null },
          { label: 'Body Fat Percentage', unit: '%', range: `${bfRef.min}-${bfRef.max}`, getValue: r => r?.bodyFat?.value !== undefined ? r.bodyFat.value : (typeof r?.bodyFat === 'object' ? null : r?.bodyFat), getStatus: r => { const val = (r?.bodyFat?.value || (typeof r?.bodyFat === 'number' ? r.bodyFat : null)); return val >= bfRef.obese ? 'high' : val < bfRef.min ? 'low' : 'normal'; } },
          // { label: 'Skeletal Muscle', unit: '%', range: '30-40', getValue: r => r?.muscleRate?.value !== undefined ? r.muscleRate.value : (typeof r?.muscleRate === 'object' ? null : r?.muscleRate), getStatus: () => null },
          { label: 'Basal Metabolic Rate', unit: 'kcal', range: '1200-2000', getValue: r => r?.bmr || null, getStatus: () => null },
          { label: 'Body Mass Index (BMI)', unit: '', range: '18.5–24.9', getValue: r => r?.bmi != null ? parseFloat(r.bmi) : null, getStatus: r => { const val = Number(r?.bmi); return val < 18.5 ? 'low' : val > 24.9 ? 'high' : 'normal'; } },
          { label: 'Subcutaneous Fat', unit: '%', range: '10-20', getValue: r => r?.bhBodyFatSubCutRate?.value !== undefined ? r.bhBodyFatSubCutRate.value : (typeof r?.bhBodyFatSubCutRate === 'object' ? null : r?.bhBodyFatSubCutRate), getStatus: () => null },
          { label: 'Visceral Fat Level', unit: '', range: '< 10', getValue: r => r?.vfal != null ? r.vfal : null, getStatus: r => (r?.vfal || 0) > 10 ? 'high' : 'normal' },
        ];

        // Prepare Chart Data mapping
        const bcChartData = [...bcArray].reverse().map(rec => ({
          date: rec?.measuredAt ? new Date(rec.measuredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '---',
          bodyFat: Number(rec?.bodyFat?.value !== undefined ? rec.bodyFat.value : rec?.bodyFat) || 0,
          muscleRate: Number(rec?.muscleRate?.value !== undefined ? rec.muscleRate.value : rec?.muscleRate) || 0,
          bmr: Number(rec?.bmr) || 0,
          bmi: Number(rec?.bmi) || 0,
          subCutFat: Number(rec?.bhBodyFatSubCutRate?.value !== undefined ? rec.bhBodyFatSubCutRate.value : rec?.bhBodyFatSubCutRate) || 0,
          vfal: Number(rec?.vfal) || 0,
        }));

        if (bcArray.length === 0) return null;

        return (
          <>
            <Accordion
              expanded={bcExpanded}
              onChange={() => setBcExpanded(!bcExpanded)}
              sx={{
                width: "100%",
                mt: 3,
                borderRadius: "8px !important",
                boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
                border: "1px solid #e5e7eb",
                backgroundColor: '#f8f8f7',
                "&:before": { display: "none" }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />}
                sx={{ p: "8px 24px", minHeight: "64px", "& .MuiAccordionSummary-content": { m: 0 } }}
              >
                <div className="flex justify-between items-center w-full pr-4">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    <Scale className="w-5 h-5 text-[color:var(--themePrimaryColor,#DA6E0B)]" />
                    Body Composition
                    <span className="ml-2 text-xs font-normal bg-orange-100 text-orange-700 px-2.5 py-0.5 rounded-full">
                      {bcArray.length} {bcArray.length === 1 ? 'record' : 'records'}
                    </span>
                  </h2>
                </div>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3, pt: 0, backgroundColor: '#f8f8f7' }}>
                <div className="flex justify-between items-center mb-4">
                  {bcArray.length > 1 && <Button
                    variant="outlined"
                    onClick={() => openAnalytics('bc')}
                    startIcon={<BarChart2 size={16} />}
                    sx={{
                      color: "var(--themePrimaryColor, #DA6E0B)",
                      borderColor: "var(--themePrimaryColor, #DA6E0B)",
                      "&:hover": { borderColor: "#B85C09", backgroundColor: "#fff8f0" },
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "6px",
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Analytics
                  </Button>}
                </div>

                <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '10px 14px', background: '#fff8f0', color: 'var(--themePrimaryColor, #DA6E0B)', fontWeight: 600, borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)', whiteSpace: 'nowrap', minWidth: 140 }}>Metric</th>
                        {bcArray.length > 0 && (
                          <th style={{
                            textAlign: 'center', padding: '12px 16px', whiteSpace: 'nowrap', borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)',
                            background: '#fff3e0', color: 'var(--themePrimaryColor, #DA6E0B)', fontWeight: 600, minWidth: '130px'
                          }}>
                            <span style={{ display: 'block', fontSize: 10, color: '#f97316', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 800 }}>Latest</span>
                            {bcArray[0].measuredAt ? new Date(bcArray[0].measuredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                            <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: '#94a3b8', marginTop: 2 }}>
                              {bcArray[0].measuredAt ? new Date(bcArray[0].measuredAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </th>
                        )}
                        {bcArray.length > 1 && (
                          <th style={{ textAlign: 'center', padding: '10px 14px', background: '#fff8f0', color: 'var(--themePrimaryColor, #DA6E0B)', fontWeight: 600, borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)', whiteSpace: 'nowrap', width: 60 }}>Trend</th>
                        )}
                        {bcArray.slice(1).map((rec, i) => (
                          <th key={i} style={{
                            textAlign: 'center', padding: '12px 16px', whiteSpace: 'nowrap', borderBottom: '2px solid var(--themePrimaryColor, #DA6E0B)',
                            background: 'transparent', color: '#64748b', fontWeight: 600, minWidth: '130px'
                          }}>
                            {rec.measuredAt ? new Date(rec.measuredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                            <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: '#94a3b8', marginTop: 2 }}>
                              {rec.measuredAt ? new Date(rec.measuredAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bcRows.map((row, ri) => (
                        <tr key={ri} style={{ background: ri % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                          <td style={{ padding: '12px 16px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9' }}>
                            <div style={{ fontWeight: 600, fontSize: '14px' }}>{row.label}</div>
                            {row.range !== '---' && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{row.range} {typeof row.unit === 'function' ? row.unit(bcArray[0]) : row.unit}</div>
                            )}
                          </td>
                          {bcArray.length > 0 && (() => {
                            const val = row.getValue(bcArray[0]);
                            const st = row.getStatus(bcArray[0]);
                            return (
                              <td style={{
                                padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #f1f5f9',
                                background: 'rgba(255,243,224,0.3)', fontWeight: 700, fontSize: '14px', color: '#1e293b'
                              }}>
                                <div className="flex flex-col items-center justify-center gap-1">
                                  {val !== null && val !== 0 && val !== '0' && val !== '' ? (
                                    <div className="flex flex-col items-center">
                                      <span style={{ fontWeight: 600, fontSize: 14 }}>
                                        {(() => {
                                          const n = Number(val);
                                          return isNaN(n) ? val : n.toFixed(2);
                                        })()}
                                      </span>
                                      {statusPill(st)}
                                    </div>
                                  ) : '---'}
                                </div>
                              </td>
                            );
                          })()}
                          {bcArray.length > 1 && (
                            <td style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9' }}>
                              {(() => {
                                const v1 = row.getValue(bcArray[0]);
                                const v2 = bcArray[1] ? row.getValue(bcArray[1]) : null;
                                const trend = (v1 && v2) ? getTrend(v2, v1) : null;
                                return trend && trend !== '→' ? (
                                  <span style={{ color: trend === '↑' ? '#ef4444' : '#22c55e', fontWeight: 900, fontSize: 16 }}>{trend}</span>
                                ) : <span style={{ color: '#94a3b8' }}>---</span>;
                              })()}
                            </td>
                          )}
                          {bcArray.slice(1).map((rec, ci) => {
                            const val = row.getValue(rec);
                            return (
                              <td key={ci} style={{
                                padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #f1f5f9',
                                background: 'transparent', fontWeight: 500, fontSize: '14px', color: '#64748b'
                              }}>
                                <div className="flex flex-col items-center justify-center gap-1">
                                  {val !== null && val !== 0 && val !== '0' && val !== '' ? (
                                    <div className="flex flex-col items-center">
                                      <span style={{ fontWeight: 600, fontSize: 14 }}>
                                        {(() => {
                                          const n = Number(val);
                                          return isNaN(n) ? val : n.toFixed(2);
                                        })()}
                                      </span>
                                    </div>
                                  ) : '---'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionDetails>
            </Accordion>
          </>
        );
      })()}


      <div>
        {Reports?.some(reportItem =>
          reportItem?.Reports?.some(report => {
            const aiAnalysis = report?.ai_analysis;

            if (
              !aiAnalysis ||
              aiAnalysis.includes('AI analysis failed') ||
              aiAnalysis.trim() === ''
            ) return false;

            try {
              const analysis = JSON.parse(aiAnalysis);
              const abnormal = analysis?.abnormal_parameters || [];
              const normal = analysis?.normal_parameters || [];
              return abnormal.length > 0 || normal.length > 0;
            } catch {
              return false;
            }
          })
        ) && (
            <div className="Prakriti-result-item">
              <p>Report Summary</p>
              <div className="w-full mt-2">
                {(() => {
                  // Helper to check if a report has valid AI analysis
                  const hasValidAiAnalysis = (reportGroup) => {
                    const nestedReport = reportGroup?.Reports?.[0];
                    return nestedReport?.ai_analysis &&
                      !nestedReport.ai_analysis.includes('AI analysis failed') &&
                      nestedReport.ai_analysis.trim() !== '';
                  };

                  const allReportGroups = Reports ?? [];
                  const validReportGroups = allReportGroups.filter(hasValidAiAnalysis);

                  const isComparisonActive = selectedBeforeReports.length > 0 && selectedAfterReports.length > 0;



                  if (isComparisonActive) {
                    const cleanJson = (str) => {
                      try { return JSON.parse(str); } catch { return null; }
                    }

                    // Collect all parameters from all selected before reports
                    const allBeforeParams = [];

                    selectedBeforeReports.forEach(reportId => {
                      const reportGroup = Reports?.find(rg => rg.id === reportId);
                      const json = cleanJson(reportGroup?.Reports?.[0]?.ai_analysis);
                      if (json) {
                        allBeforeParams.push(...(json.abnormal_parameters || []), ...(json.normal_parameters || []));
                      }
                    });

                    // Collect all parameters from all selected after reports
                    const allAfterParams = [];

                    selectedAfterReports.forEach(reportId => {
                      const reportGroup = Reports?.find(rg => rg.id === reportId);
                      const json = cleanJson(reportGroup?.Reports?.[0]?.ai_analysis);
                      if (json) {
                        allAfterParams.push(...(json.abnormal_parameters || []), ...(json.normal_parameters || []));
                      }
                    });

                  }


                  const collected =
                    Reports?.flatMap(ri => ri?.Reports ?? [])?.flatMap((report, rIdx) => {
                      const aiAnalysis = report?.ai_analysis;
                      if (
                        !aiAnalysis ||
                        aiAnalysis.includes('AI analysis failed') ||
                        aiAnalysis.trim() === ''
                      ) return [];

                      let analysis;
                      try {
                        analysis = JSON.parse(aiAnalysis);
                      } catch {
                        return [];
                      }

                      const abnormal = (analysis?.abnormal_parameters ?? []).map((p, i) => ({
                        ...p,
                        type: 'abnormal',
                        _k: `ab-${rIdx}-${i}-${p?.name ?? 'x'}`
                      }));

                      const normal = (analysis?.normal_parameters ?? []).map((p, i) => ({
                        ...p,
                        type: 'normal',
                        _k: `no-${rIdx}-${i}-${p?.name ?? 'x'}`
                      }));

                      return [...abnormal, ...normal];
                    }) ?? [];

                  const abnormalGroup = collected.filter(p => p.type === 'abnormal');
                  const normalGroup = collected.filter(p => p.type === 'normal');

                  const Group = ({ title, items }) => {
                    if (!items?.length) return null;

                    return (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}>
                          {title} ({items.length})
                        </Typography>

                        {items.map((param, j) => {
                          const status = (param?.status || '').toLowerCase();
                          const backgroundColor =
                            status === 'low' || status === 'high'
                              ? '#FFF5F5'
                              : status === 'normal'
                                ? 'var(--themeSecondaryColor, #F1EFEC)'
                                : '#F5F5F5';

                          return (
                            <Accordion
                              key={param?._k ?? `${title}-${j}`}
                              sx={{ backgroundColor, color: '#7C7870', mb: 1 }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box className="flex items-center gap-2 w-full" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 400 }}>
                                  {getIcon?.(status)}
                                  <Box className="flex items-center justify-between gap-2 w-full">
                                    <Box className="flex items-center gap-2">
                                      <span>{param?.name}</span>
                                      {param?.value !== undefined && (
                                        <Typography variant="body2">({param?.value})</Typography>
                                      )}
                                    </Box>
                                    {getStatusTag?.(status)}
                                  </Box>
                                </Box>
                              </AccordionSummary>

                              <AccordionDetails>
                                {param?.reference_range && (
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 400, color: '#333' }}
                                  >
                                    <strong>Reference Range:</strong> {param.reference_range}
                                  </Typography>
                                )}

                                {!!param?.reason && (
                                  <Typography
                                    variant="body2"
                                    className="mt-2"
                                    sx={{ fontFamily: "'Poppins', sans-serif", color: '#555' }}
                                  >
                                    <strong>Note:</strong> {param.reason}
                                  </Typography>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Box>
                    );
                  };

                  return (
                    <Box sx={{ mb: 2 }}>
                      {validReportGroups.length > 0 && (
                        <>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 3,
                            p: 2.5,
                            background: 'linear-gradient(135deg, #fff8f0 0%, #ffe8d6 100%)',
                            borderRadius: 3,
                            border: '1px solid var(--themePrimaryColor, #DA6E0B)',
                            boxShadow: '0 4px 12px rgba(218, 110, 11, 0.1)'
                          }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--themePrimaryColor, #DA6E0B)', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>Before Treatment ({selectedBeforeReports.length})</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {selectedBeforeReports.length === 0 ? (
                                    <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: "'Poppins', sans-serif" }}>None Selected</Typography>
                                  ) : (
                                    selectedBeforeReports.map(id => (
                                      <Chip
                                        key={id}
                                        label={validReportGroups.find(rg => rg.id === id)?.ReportType}
                                        size="small"
                                        onDelete={() => setSelectedBeforeReports(prev => prev.filter(rid => rid !== id))}
                                        sx={{
                                          backgroundColor: '#4CAF50',
                                          color: 'white',
                                          fontWeight: 500,
                                          fontFamily: "'Poppins', sans-serif",
                                          '& .MuiChip-deleteIcon': { color: 'white' }
                                        }}
                                      />
                                    ))
                                  )}
                                </Box>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--themePrimaryColor, #DA6E0B)', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>After Treatment ({selectedAfterReports.length})</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {selectedAfterReports.length === 0 ? (
                                    <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: "'Poppins', sans-serif" }}>None Selected</Typography>
                                  ) : (
                                    selectedAfterReports.map(id => (
                                      <Chip
                                        key={id}
                                        label={validReportGroups.find(rg => rg.id === id)?.ReportType}
                                        size="small"
                                        onDelete={() => setSelectedAfterReports(prev => prev.filter(rid => rid !== id))}
                                        sx={{
                                          backgroundColor: '#2196F3',
                                          color: 'white',
                                          fontWeight: 500,
                                          fontFamily: "'Poppins', sans-serif",
                                          '& .MuiChip-deleteIcon': { color: 'white' }
                                        }}
                                      />
                                    ))
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            <Button
                              variant="contained"
                              onClick={() => setOpenCompareReportDialog(true)}
                              sx={{
                                textTransform: 'none',
                                backgroundColor: 'var(--themePrimaryColor, #DA6E0B)',
                                color: 'white',
                                fontWeight: 600,
                                fontFamily: "'Poppins', sans-serif",
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(218, 110, 11, 0.25)',
                                '&:hover': {
                                  backgroundColor: '#c46009',
                                  boxShadow: '0 6px 16px rgba(218, 110, 11, 0.35)'
                                }
                              }}
                            >
                              Select Reports
                            </Button>
                          </Box>

                          <Dialog
                            open={openCompareReportDialog}

                            onClose={() => setOpenCompareReportDialog(false)}
                            maxWidth="md"
                            fullWidth
                          >
                            <DialogTitle sx={{
                              fontWeight: 600,
                              fontFamily: "'Poppins', sans-serif",
                              background: 'linear-gradient(135deg, #fff8f0 0%, #ffe8d6 100%)',
                            }}>Select Reports for Comparison</DialogTitle>
                            <DialogContent>
                              <Grid container spacing={2} sx={{ mt: 1 }} alignItems="stretch">
                                {allReportGroups.map((reportGroup) => {
                                  const currentSelection = getReportSelection(reportGroup.id);
                                  const isSelected = currentSelection !== null;
                                  const canCompare = hasValidAiAnalysis(reportGroup);

                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      key={reportGroup.id}
                                      sx={{ display: 'flex' }}
                                    >
                                      <Card
                                        variant="outlined"
                                        sx={{
                                          height: '100%',
                                          width: '100%',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          borderColor: isSelected ? 'var(--themePrimaryColor, #DA6E0B)' : '#e0e0e0',
                                          backgroundColor: isSelected ? '#fff8f0' : 'white',
                                          opacity: canCompare ? 1 : 0.5,
                                          borderWidth: isSelected ? 2 : 1,
                                          boxShadow: isSelected
                                            ? '0 4px 16px rgba(218, 110, 11, 0.2)'
                                            : '0 2px 8px rgba(0,0,0,0.05)',
                                          transition: 'all 0.3s ease',
                                          '&:hover': {
                                            boxShadow: canCompare
                                              ? '0 6px 20px rgba(218, 110, 11, 0.15)'
                                              : 'none',
                                            transform: canCompare ? 'translateY(-2px)' : 'none'
                                          }
                                        }}
                                      >
                                        <CardContent sx={{ flexGrow: 1 }}>
                                          <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                            {reportGroup.ReportType}
                                          </Typography>

                                          <Typography variant="caption" color="textSecondary" display="block">
                                            Date: {new Date(reportGroup.CreatedAt).toLocaleDateString()}
                                          </Typography>

                                          <Typography variant="caption" color="textSecondary" display="block">
                                            Uploaded by: {reportGroup.ReportUploadedby || 'Unknown'}
                                          </Typography>

                                          {reportGroup.Comments && (
                                            <Typography
                                              variant="caption"
                                              color="textSecondary"
                                              display="block"
                                              sx={{ mt: 1, fontStyle: 'italic' }}
                                            >
                                              {reportGroup.Comments}
                                            </Typography>
                                          )}

                                          {!canCompare && (
                                            <Typography
                                              variant="caption"
                                              color="error"
                                              display="block"
                                              sx={{ mt: 1, fontWeight: 'bold' }}
                                            >
                                              No AI analysis available
                                            </Typography>
                                          )}
                                        </CardContent>

                                        <CardActions> <Stack direction="row" spacing={1}> <Chip label="Before" size="small" disabled={!canCompare || currentSelection === 'after'} onClick={() => { if (currentSelection === 'before') { setSelectedBeforeReports(prev => prev.filter(id => id !== reportGroup.id)); } else { setSelectedBeforeReports(prev => [...prev, reportGroup.id]); } }} sx={{ backgroundColor: currentSelection === 'before' ? '#4CAF50' : 'transparent', color: currentSelection === 'before' ? 'white' : '#4CAF50', borderColor: '#4CAF50', borderWidth: 1, borderStyle: 'solid', fontWeight: 600, fontFamily: "'Poppins', sans-serif", '&:hover': { backgroundColor: currentSelection === 'before' ? '#45a049' : 'rgba(76, 175, 80, 0.1)' } }} /> <Chip label="After" size="small" disabled={!canCompare || currentSelection === 'before'} onClick={() => { if (currentSelection === 'after') { setSelectedAfterReports(prev => prev.filter(id => id !== reportGroup.id)); } else { setSelectedAfterReports(prev => [...prev, reportGroup.id]); } }} sx={{ backgroundColor: currentSelection === 'after' ? '#2196F3' : 'transparent', color: currentSelection === 'after' ? 'white' : '#2196F3', borderColor: '#2196F3', borderWidth: 1, borderStyle: 'solid', fontWeight: 600, fontFamily: "'Poppins', sans-serif", '&:hover': { backgroundColor: currentSelection === 'after' ? '#1976D2' : 'rgba(33, 150, 243, 0.1)' } }} /> </Stack> </CardActions>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>

                            </DialogContent>
                            <DialogActions>
                              <Button onClick={() => setOpenCompareReportDialog(false)}>Close</Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}


                      {isComparisonActive ? (
                        <TableContainer
                          component={Paper}
                          elevation={2}
                          sx={{
                            borderRadius: 3,
                            overflow: 'auto',
                            border: '1px solid',
                            borderColor: '#e5e7eb',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                        >
                          <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ ...headerCellStyle, width: '25%', position: 'sticky', left: 0, zIndex: 10, bgcolor: '#fff8f0' }}>Parameter</TableCell>
                                {comparisonData.reportDates.map((rd, i) => (
                                  <TableCell key={i} align="center" sx={{ ...headerCellStyle, minWidth: 100 }}>
                                    <div style={{ fontSize: 13 }}>{rd.dateStr}</div>
                                    <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{rd.year}</div>
                                  </TableCell>
                                ))}
                                <TableCell align="center" sx={headerCellStyle}>Ref. Range</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {comparisonData.rows.map((row, idx) => (
                                <TableRow
                                  key={idx}
                                  hover
                                  sx={{
                                    backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                                    '&:hover': { backgroundColor: '#f1f5f9' }
                                  }}
                                >
                                  {/* PARAMETER */}
                                  <TableCell sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b', borderRight: '1px solid #f1f5f9', position: 'sticky', left: 0, zIndex: 5, bgcolor: idx % 2 === 0 ? '#fafafa' : 'white' }}>
                                    {row.name}
                                    {row.unit && (
                                      <Typography component="span" sx={{ fontSize: 11, color: '#94a3b8', ml: 0.5, fontWeight: 400 }}>
                                        ({typeof row.unit === 'object' ? (row.unit.unit || JSON.stringify(row.unit)) : row.unit})
                                      </Typography>
                                    )}
                                  </TableCell>

                                  {/* VALUES */}
                                  {row.values.map((v, ci) => {
                                    const prevV = ci > 0 ? row.values[ci - 1] : null;
                                    const trend = (v?.value && prevV?.value) ? getTrend(prevV.value, v.value) : null;

                                    return (
                                      <TableCell key={ci} align="center" sx={{ borderRight: '1px solid #f1f5f9' }}>
                                        <div className="flex flex-col items-center justify-center">
                                          {v?.value != null ? (
                                            <div className="flex items-center gap-1.5">
                                              <span style={{ fontWeight: 600, fontSize: 14, color: '#334155' }}>{v.value}</span>
                                              {trend && trend !== '→' && (
                                                <span style={{
                                                  fontSize: 16,
                                                  color: trend === '↑' ? '#ef4444' : '#22c55e',
                                                  fontWeight: 900,
                                                  lineHeight: 1
                                                }}>
                                                  {trend}
                                                </span>
                                              )}
                                              {getStatusTag(v.status)}
                                            </div>
                                          ) : (
                                            <span style={{ color: '#cbd5e1' }}>---</span>
                                          )}
                                        </div>
                                      </TableCell>
                                    );
                                  })}

                                  {/* RANGE */}
                                  <TableCell sx={{ color: '#64748b', fontSize: 12, textAlign: 'center' }}>
                                    {row.refRange || '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                      ) : (
                        <>
                          <Group title="Abnormal" items={abnormalGroup} />
                          <Group title="Normal" items={normalGroup} />
                        </>
                      )}
                    </Box>
                  );
                })()}
              </div>
            </div>
          )}
      </div>



      <Dialog fullScreen open={open5} onClose={handleClose5} fullWidth>
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Vikriti Assessment Result
            </Typography>
            <IconButton onClick={handleClose5}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <div className="PrakritiAssesmentEdit">
          <TableContainer
            component={Paper}
            style={{ marginTop: "20px", borderRadius: "10px" }}
            className="AppointmentTable"
          >
            <Table>
              <TableHead>
                <TableRow className="tableheadtitle" align="center">
                  {/* <TableCell>
                    <strong>Category</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Sub-Category</strong>
                  </TableCell> */}
                  <TableCell>
                    <strong>Symptom</strong>
                  </TableCell>
                  {/* <TableCell>
                    <strong>Dosha Increase</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Dosha Decrease</strong>
                  </TableCell> */}
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user?.Vikriti_details?.vikritiList?.map((item, index) => (
                  <TableRow key={index}>
                    {/* <TableCell>
                      <TextField
                        value={item.category}
                        onChange={(e) =>
                          handleEditChange(index, "category", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.sub_category}
                        onChange={(e) =>
                          handleEditChange(
                            index,
                            "sub_category",
                            e.target.value
                          )
                        }
                      />
                    </TableCell> */}
                    <TableCell sx={{ minWidth: 400 }}>
                      <Autocomplete
                        freeSolo
                        options={
                          symptomsList
                            ? symptomsList
                              .map((symptom) => symptom.toLowerCase())
                              .slice(0, 10000)
                            : []
                        }
                        value={item.symptoms}
                        onInputChange={(event, newSearchTerm) => {
                          setSearchTerm(newSearchTerm.toLowerCase());
                        }}
                        onChange={(event, newValue) => {

                          const selectedData = vikritidata.find(
                            (data) =>
                              data.symptoms.toLowerCase() ===
                              newValue?.toLowerCase()
                          );

                          if (selectedData) {
                            handleEditChange(
                              index,
                              "symptoms",
                              selectedData.symptoms
                            );

                            handleEditChange(
                              index,
                              "category",
                              selectedData.category
                            );

                            handleEditChange(
                              index,
                              "sub_category",
                              selectedData.sub_category
                            );

                            handleEditChange(
                              index,
                              "dosha_increase",
                              selectedData.dosha_increase
                            );

                            handleEditChange(
                              index,
                              "dosha_decrease",
                              selectedData.dosha_decrease
                            );

                            handleEditChange(
                              index,
                              "dhatu_increase",
                              selectedData.dhatu_increase
                            );

                            handleEditChange(
                              index,
                              "dhatu_decrease",
                              selectedData.dhatu_decrease
                            );

                            handleEditChange(
                              index,
                              "mala_increase",
                              selectedData.mala_increase
                            );

                            handleEditChange(
                              index,
                              "mala_decrease",
                              selectedData.mala_decrease
                            );

                          } else {
                            handleEditChange(index, "symptoms", newValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Symptoms"
                            variant="outlined"
                          />
                        )}
                        filterOptions={(options, { inputValue }) => {
                          const lowerInput = inputValue.toLowerCase();
                          return symptomsList.filter(
                            (symptom) =>
                              symptom.toLowerCase().includes(lowerInput) // Match in lowercase
                          );
                        }}
                      />
                    </TableCell>
                    {/* <TableCell sx={{ minWidth: 150 }}>
                      <TextField
                        value={item.dosha_increase}
                        onChange={(e) =>
                          handleEditChange(
                            index,
                            "dosha_increase",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField
                        value={item.dosha_decrease}
                        onChange={(e) =>
                          handleEditChange(
                            index,
                            "dosha_decrease",
                            e.target.value
                          )
                        }
                      />
                    </TableCell> */}
                    <TableCell>
                      <IconButton onClick={() => handleRemove(index)}>
                        <HighlightOffIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            onClick={handleAdd}
            style={{
              marginTop: "10px",
              marginRight: "10px",
              background: "var(--themePrimaryColor, #DA6E0B)",
            }}
          >
            Add Row
          </Button>
          <Button
            variant="contained"
            onClick={handleVikritiSavechanges}
            style={{ marginTop: "10px", background: "var(--themePrimaryColor, #DA6E0B)" }}
          >
            Save Changes
          </Button>
        </div>
      </Dialog>

      <Dialog fullScreen open={open} onClose={handleClose} fullWidth>
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Prakriti Assessment Result
            </Typography>

            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>

          </div>
        </DialogTitle>

        {/* <div className="PrakritiAssesmentEdit">
          {user?.Prakriti_details?.PrakritiCollection.map(
            (question, questionIndex) => {
              const selectedAnswerIndex = question.Answers.findIndex(
                (answer) => answer.isSelected
              );
              const selectedAnswer = question.Answers[selectedAnswerIndex];
              return (
                <div
                  className="PrakritiAssesmentEdit-container"
                  key={questionIndex}
                >
                  <div>
                    <p>{question.Question}</p>
                  </div>
                  <div>
                    {editQuestionIndex === questionIndex ? (
                      <FormControl fullWidth>
                        <Select
                          value={selectedAnswerIndex}
                          onChange={(e) =>
                            handleAnswerChange(
                              questionIndex,
                              parseInt(e.target.value, 10)
                            )
                          }
                        >
                          {question.Answers.map((answer, answerIndex) => (
                            <MenuItem key={answerIndex} value={answerIndex}>
                              {answer.Answer}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      selectedAnswer && (
                        <div className="answer-item selected">
                          <div className="answer-content">
                            <img
                              src={selectedAnswer.image}
                              alt={selectedAnswer.AnswerTag}
                              className="answer-image"
                            />
                            <p>{selectedAnswer.Answer}</p>
                          </div>
                          <div className="answer-actions">
                            <CheckCircle
                              style={{ marginRight: "10px", color: "#8CEB8C" }}
                            />
                            {selectedAnswer.vpk && (
                              <div className="vpk-indicator">
                                <span>{selectedAnswer.vpk.toUpperCase()}</span>
                                <ArrowUpward
                                  fontSize="small"
                                  style={{
                                    color: "var(--themePrimaryColor, #3B362A)",
                                  }}
                                />
                              </div>
                            )}
                            <Edit
                              className="edit-answer-button"
                              onClick={() => handleEditAnswer(questionIndex)}
                              style={{
                                cursor: "pointer",
                                color: "var(--themePrimaryColor, #312110)",
                              }}
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            }
          )}

          <div className="flex flex-row items-center justify-center m-3">
            <Button
              onClick={handleClick}
              sx={{
                textTransform: "none",
                borderRadius: "0",
                backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
                color: "white",
                border: "none",
                boxSizing: "border-box",
                borderRadius: "5px",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
                },
              }}
            >
              Save Changes
            </Button>
          </div>
        </div> */}

        <div className="PrakritiAssesmentEdit">
          {Array.isArray(user?.Prakriti_details?.PrakritiCollection) &&
            user.Prakriti_details.PrakritiCollection.map((question, questionIndex) => {
              const selectedAnswerIndex = question?.Answers?.findIndex(
                (answer) => answer?.isSelected
              );
              const selectedAnswer = question?.Answers[selectedAnswerIndex];
              return (
                <div
                  className="PrakritiAssesmentEdit-container"
                  key={questionIndex}
                >
                  <div>
                    <p>{question?.Question}</p>
                  </div>
                  <div>
                    {editQuestionIndex === questionIndex ? (
                      <FormControl fullWidth>
                        <Select
                          value={selectedAnswerIndex}
                          onChange={(e) =>
                            handleAnswerChange(
                              questionIndex,
                              parseInt(e.target.value, 10)
                            )
                          }
                        >
                          {question.Answers.map((answer, answerIndex) => (
                            <MenuItem key={answerIndex} value={answerIndex}>
                              {answer.Answer}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      selectedAnswer && (
                        <div className="answer-item selected">
                          <div className="answer-content">
                            <img
                              src={selectedAnswer.image}
                              alt={selectedAnswer.AnswerTag}
                              className="answer-image"
                            />
                            <p>{selectedAnswer.Answer}</p>
                          </div>
                          <div className="answer-actions">
                            <CheckCircle
                              style={{ marginRight: "10px", color: "#8CEB8C" }}
                            />
                            {selectedAnswer.vpk && (
                              <div className="vpk-indicator">
                                <span>{selectedAnswer.vpk.toUpperCase()}</span>
                                <ArrowUpward
                                  fontSize="small"
                                  style={{
                                    color: "var(--themePrimaryColor, #3B362A)",
                                  }}
                                />
                              </div>
                            )}
                            <Edit
                              className="edit-answer-button"
                              onClick={() => handleEditAnswer(questionIndex)}
                              style={{
                                cursor: "pointer",
                                color: "var(--themePrimaryColor, #312110)",
                              }}
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}

          <div className="flex flex-row items-center justify-center m-3">
            <Button
              onClick={handleClick}
              sx={{
                textTransform: "none",
                borderRadius: "5px",
                backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
                color: "white",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
                },
              }}
            >
              Save Changes
            </Button>
          </div>

        </div>

      </Dialog>

      <Dialog open={open4} onClose={handleClose4} fullWidth maxWidth="sm">
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Prakriti Assessment Result
            </Typography>
            <IconButton onClick={handleClose4}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <div style={{ padding: "16px", textAlign: "center" }}>
          <div>
            <TextField
              id="standard-search"
              label="Dominant"
              value={findVPKResult([
                user?.Prakriti_details?.vata,
                user?.Prakriti_details?.pitta,
                user?.Prakriti_details?.kapha,
              ])}
              type="search"
              variant="standard"
            />
          </div>
          <div className="pinput-c">
            {/* Vata */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#FF9800",
                    marginRight: "8px",
                  }}
                ></div>
                Vata
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={
                  user?.Prakriti_details?.vata === 0
                    ? "0"
                    : user?.Prakriti_details?.vata != null
                      ? Math.round(user.Prakriti_details.vata)
                      : ""
                }
                onChange={(e) => {
                  const input = e.target.value.trim();
                  const newValue = input === "" ? "" : parseInt(input, 10);

                  const currentPitta = parseInt(user?.Prakriti_details?.pitta || 0);
                  const currentKapha = parseInt(user?.Prakriti_details?.kapha || 0);
                  const total = (newValue || 0) + currentPitta + currentKapha;

                  if (total <= 100 || input === "") {
                    setuser((prevState) => ({
                      ...prevState,
                      Prakriti_details: {
                        ...prevState.Prakriti_details,
                        vata: newValue,
                      },
                    }));
                  }
                }}
              />
            </div>

            {/* Pitta */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#795548",
                    marginRight: "8px",
                  }}
                ></div>
                Pitta
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={
                  user?.Prakriti_details?.pitta === 0
                    ? "0"
                    : user?.Prakriti_details?.pitta != null
                      ? Math.round(user.Prakriti_details.pitta)
                      : ""
                }
                onChange={(e) => {
                  const input = e.target.value.trim();
                  const newValue = input === "" ? "" : parseInt(input, 10);

                  const currentVata = parseInt(user?.Prakriti_details?.vata || 0);
                  const currentKapha = parseInt(user?.Prakriti_details?.kapha || 0);
                  const total = (newValue || 0) + currentVata + currentKapha;

                  if (total <= 100 || input === "") {
                    setuser((prevState) => ({
                      ...prevState,
                      Prakriti_details: {
                        ...prevState.Prakriti_details,
                        pitta: newValue,
                      },
                    }));
                  }
                }}
              />
            </div>

            {/* Kapha */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#8DE995",
                    marginRight: "8px",
                  }}
                ></div>
                Kapha
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={
                  user?.Prakriti_details?.kapha === 0
                    ? "0"
                    : user?.Prakriti_details?.kapha != null
                      ? Math.round(user.Prakriti_details.kapha)
                      : ""
                }
                onChange={(e) => {
                  const input = e.target.value.trim();
                  const newValue = input === "" ? "" : parseInt(input, 10);

                  const currentVata = parseInt(user?.Prakriti_details?.vata || 0);
                  const currentPitta = parseInt(user?.Prakriti_details?.pitta || 0);
                  const total = (newValue || 0) + currentVata + currentPitta;

                  if (total <= 100 || input === "") {
                    setuser((prevState) => ({
                      ...prevState,
                      Prakriti_details: {
                        ...prevState.Prakriti_details,
                        kapha: newValue,
                      },
                    }));
                  }
                }}
              />
            </div>
          </div>


          <Button
            sx={{
              textTransform: "none",
              borderRadius: "0",
              backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
              color: "white",
              border: "none",
              boxSizing: "border-box",
              borderRadius: "5px",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              padding: "10px",
              "&:hover": {
                backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
              },
            }}
            onClick={EditPrakritiResult}
          >
            Save
          </Button>
        </div>

      </Dialog>

      <Dialog open={open3} onClose={handleClose3} fullWidth maxWidth="sm">
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Vikriti Assessment Result
            </Typography>
            <IconButton onClick={handleClose3}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <div style={{ padding: "16px", textAlign: "center" }}>
          <div>
            <TextField
              id="standard-search"
              label="Dominant"
              value={user?.Vikriti_details?.Result}
              type="search"
              onChange={(e) => {
                const newValue = e.target.value;
                setuser((prevState) => ({
                  ...prevState,
                  Vikriti_details: {
                    ...prevState.Vikriti_details,
                    Result: newValue,
                  },
                }));
              }}
              variant="standard"
            />
          </div>

          <div className="pinput-c">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#FF9800",
                    marginRight: "8px",
                  }}
                ></div>
                Vata
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={user?.Vikriti_details?.vataAssScore}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setuser((prevState) => ({
                    ...prevState,
                    Vikriti_details: {
                      ...prevState?.Vikriti_details,
                      vataAssScore: parseInt(newValue),
                    },
                  }));
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#795548",
                    marginRight: "8px",
                  }}
                ></div>
                Pitta
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={user?.Vikriti_details?.pittaAssScore}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setuser((prevState) => ({
                    ...prevState,
                    Vikriti_details: {
                      ...prevState?.Vikriti_details,
                      pittaAssScore: parseInt(newValue),
                    },
                  }));
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#8DE995",
                    marginRight: "8px",
                  }}
                ></div>
                Kapha
              </div>

              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={user?.Vikriti_details?.kaphaAssScore}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setuser((prevState) => ({
                    ...prevState,
                    Vikriti_details: {
                      ...prevState?.Vikriti_details,
                      kaphaAssScore: parseInt(newValue),
                    },
                  }));
                }}
              />
            </div>
          </div>

          <Button
            sx={{
              textTransform: "none",
              borderRadius: "0",
              backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
              color: "white",
              border: "none",
              boxSizing: "border-box",
              borderRadius: "5px",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              padding: "10px",
              "&:hover": {
                backgroundColor: "var(--themePrimaryColor, #DA6E0B)",
              },
            }}
            onClick={UpdateVkikriti}
          >
            Save
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <div className="flex justify-between items-center">
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Generated Report
            </Typography>
            <IconButton onClick={() => setOpenReportDialog(false)}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {reportUrl ? (
            <iframe
              src={reportUrl}
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="Report Preview"
            />
          ) : (
            <div className="flex justify-center items-center h-64">
              <Typography>No report available</Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportDialog(false)} color="secondary">
            Close
          </Button>
          {reportUrl && (
            <Button
              variant="contained"
              sx={{
                bgcolor: "var(--themePrimaryColor, #DA6E0B)",
                "&:hover": { bgcolor: "#B85C09" },
                color: "white",
              }}
              onClick={handleDownloadReport}
            >
              Download Report
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewReportDialogOpen}
        onClose={() => setViewReportDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <div className="flex justify-between items-center">
            <Typography variant="h6" style={{ fontWeight: "bold", fontFamily: "'Poppins', sans-serif" }}>
              Generated Reports
            </Typography>
            <IconButton onClick={() => setViewReportDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#f8fafc", p: 3 }}>
          {user?.kioskreport?.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {[...user.kioskreport]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((report, idx) => {
                  const reportDate = new Date(report.date);
                  const formattedDate = !isNaN(reportDate) ? reportDate.toLocaleDateString('en-US', {
                    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                  }) : report.date;

                  return (
                    <Card
                      key={idx}
                      elevation={0}
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                          borderColor: "#cbd5e1"
                        }
                      }}
                    >
                      <CardContent sx={{ p: "20px !important", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                            <Activity className="text-[color:var(--themePrimaryColor,#DA6E0B)] w-6 h-6" />
                          </div>
                          <div>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif", color: "#0f172a", lineHeight: 1.2, mb: 0.5 }}>
                              Assessment Report
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#64748b", fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem" }}>
                              Generated on {formattedDate}
                            </Typography>
                          </div>
                        </div>
                        <Button
                          variant="contained"
                          disableElevation
                          onClick={() => {
                            if (report.Data?.kioskreport) {
                              window.open(report.Data.kioskreport, "_blank");
                            } else if (report.Data?.Report) {
                              window.open(report.Data.Report, "_blank");
                            } else {
                              alert("No report URL found.");
                            }
                          }}
                          sx={{
                            bgcolor: "var(--themePrimaryColor, #DA6E0B)",
                            color: "white",
                            "&:hover": { bgcolor: "#B85C09" },
                            textTransform: "none",
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 500,
                            borderRadius: "8px",
                            px: 3,
                            py: 1
                          }}
                        >
                          View PDF
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-48 bg-[color:var(--themeSurfaceColor,#FFFFFF)] rounded-xl border border-dashed border-[color:var(--themePrimaryColor,#d1d5db)]">
              <Activity className="w-10 h-10 text-gray-300 mb-3" />
              <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: "#64748b", fontWeight: 500 }}>
                No assessment reports available
              </Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>



    </div>

  );
}

export default Patient_Profile;

